import axios from 'axios';
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/lib/AuthContext";
import { usePermissions } from "@/lib/PermissionsContext";
import { toast } from "sonner";
import { NormesMaterielDialog } from "@/components/normes/NormesMaterielDialog";
import { Car, Plus, ChevronDown, AlertTriangle, Wrench, Camera, X, CheckCircle, Clock, RefreshCw, Trash2, Edit, Save, Package, Hash, Printer } from "lucide-react";
import { formatLocalDate, formatLocalTime } from "@/utils/formatLocalTime"; // 🆕 [P1 ext]
import { dymoPrintHuileLabel } from "@/lib/dymoPrint";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EchafaudsInventaireUI } from "@/components/echafauds/EchafaudsInventaireUI";
import { InspectionRangementSection } from "@/components/InspectionRangementSection";
import { InspectionRangementHubDialog } from "@/components/InspectionRangementHubDialog";
import { ClipboardCheck } from "lucide-react";
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples

const API = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const TYPES_ENTRETIEN = [
  "Changement d'huile", "Vidange d'huile", "Changement de pneus", "Inspection annuelle",
  "Freins", "Batterie", "Courroie", "Filtre à air",
  "Climatisation", "Transmission", "Autre",
];

// 🛢️ [15] Types déclencheurs de l'étiquette "Prochain changement d'huile"
const TYPES_HUILE = new Set(["Changement d'huile", "Vidange d'huile"]);

const CONDITION_OPTS = [
  { value: "bon", label: "Bon état", color: "bg-emerald-100 text-emerald-700" },
  { value: "use", label: "Usé", color: "bg-amber-100 text-amber-700" },
  { value: "a_remplacer", label: "À remplacer", color: "bg-red-100 text-red-700" },
];

function conditionBadge(cond) {
  const opt = CONDITION_OPTS.find(o => o.value === cond) || CONDITION_OPTS[0];
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${opt.color}`}>{opt.label}</span>;
}

// Card styles constants
const CARD_BASE = "bg-white border rounded-xl transition-all duration-200 overflow-hidden";
const CARD_IDLE = "border-slate-200 hover:border-slate-300 hover:shadow-sm";
const ICON_BOX = "w-10 h-10 rounded-xl flex items-center justify-center shrink-0";

// ── Formulaire véhicule ──────────────────────────────────────────────────────
function VehiculeForm({ initial, onSave, onCancel }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [form, setForm] = useState(initial || {
    nom: "", numero_unite: "", marque: "", modele: "", annee: new Date().getFullYear(),
    plaque: "", numero_serie: "", kilometrage: 0, couleur: "",
    assurance_expiration: "", inspection_expiration: "", notes: "",
    assigne_a: null,
    pneus_hiver: { marque: "", taille: "", annee_achat: null, condition: "bon" },
    pneus_ete: { marque: "", taille: "", annee_achat: null, condition: "bon" },
  });
  const [assignOptions, setAssignOptions] = useState([]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setPneu = (type, k, v) => setForm(f => ({ ...f, [type]: { ...f[type], [k]: v } }));

  useEffect(() => {
    const load = async () => {
      try {
        const [empRes, lmdsRes] = await Promise.all([
          axios.get(`${API}/api/employees`).then(r => r.data),
          axios.get(`${API}/api/lmds/installers`).then(r => r.data),
        ]);
        const emps = (empRes.employees || []).map(e => ({ type: 'employe', id: e.id, nom: `${e.prenom} ${e.nom}` }));
        const lmds = (lmdsRes.installers || lmdsRes || []).map(i => ({ type: 'sous_traitant', id: i.id, nom: `${i.prenom || ''} ${i.nom || ''}`.trim() || i.id }));
        setAssignOptions([...emps, ...lmds]);
      } catch (e) {
        console.error('Erreur chargement assignables:', e);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs font-medium text-slate-600">Nom / Surnom *</label>
          <Input value={form.nom} onChange={e => set("nom", e.target.value)} placeholder="F-150 Principal" className="mt-1" /></div>
        <div><label className="text-xs font-medium text-slate-600"># d'unité</label>
          <Input value={form.numero_unite || ''} onChange={e => set("numero_unite", e.target.value)} placeholder="U-001" className="mt-1 font-mono" /></div>
        <div><label className="text-xs font-medium text-slate-600">Marque *</label>
          <Input value={form.marque} onChange={e => set("marque", e.target.value)} placeholder="Ford" className="mt-1" /></div>
        <div><label className="text-xs font-medium text-slate-600">Modèle *</label>
          <Input value={form.modele} onChange={e => set("modele", e.target.value)} placeholder="F-150" className="mt-1" /></div>
        <div><label className="text-xs font-medium text-slate-600">Année</label>
          <Input type="number" value={form.annee} onChange={e => set("annee", parseInt(e.target.value))} className="mt-1" /></div>
        <div><label className="text-xs font-medium text-slate-600">Plaque *</label>
          <Input value={form.plaque} onChange={e => set("plaque", e.target.value.toUpperCase())} placeholder="A12 B34" className="mt-1 font-mono" /></div>
        <div><label className="text-xs font-medium text-slate-600">N° de série</label>
          <Input value={form.numero_serie} onChange={e => set("numero_serie", e.target.value)} placeholder="1FTEW1E50KFB00000" className="mt-1 font-mono" /></div>
        <div><label className="text-xs font-medium text-slate-600">Kilométrage</label>
          <Input type="number" value={form.kilometrage} onChange={e => set("kilometrage", parseInt(e.target.value) || 0)} className="mt-1" /></div>
        <div><label className="text-xs font-medium text-slate-600">Couleur</label>
          <Input value={form.couleur} onChange={e => set("couleur", e.target.value)} placeholder="Blanc" className="mt-1" /></div>
        <div><label className="text-xs font-medium text-slate-600">Assurance (expiration)</label>
          <Input type="date" value={form.assurance_expiration || ""} onChange={e => set("assurance_expiration", e.target.value)} className="mt-1" /></div>
        <div><label className="text-xs font-medium text-slate-600">Inspection (expiration)</label>
          <Input type="date" value={form.inspection_expiration || ""} onChange={e => set("inspection_expiration", e.target.value)} className="mt-1" /></div>
      </div>
      {/* Assignation */}
      <div>
        <label className="text-xs font-medium text-slate-600">Assigné à</label>
        <Select
          value={form.assigne_a ? `${form.assigne_a.type}:${form.assigne_a.id}` : "__none__"}
          onValueChange={val => {
            if (val === "__none__") { set("assigne_a", null); return; }
            const opt = assignOptions.find(o => `${o.type}:${o.id}` === val);
            if (opt) set("assigne_a", { type: opt.type, id: opt.id, nom: opt.nom });
          }}
        >
          <SelectTrigger className="mt-1" data-testid="select-assign-unite"><SelectValue placeholder="Non assigné" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Non assigné</SelectItem>
            {assignOptions.filter(o => o.type === 'employe').length > 0 && (
              <>
                <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase">Employés</div>
                {assignOptions.filter(o => o.type === 'employe').map(o => (
                  <SelectItem key={`e:${o.id}`} value={`employe:${o.id}`}>{o.nom}</SelectItem>
                ))}
              </>
            )}
            {assignOptions.filter(o => o.type === 'sous_traitant').length > 0 && (
              <>
                <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase">Sous-traitants LMDS</div>
                {assignOptions.filter(o => o.type === 'sous_traitant').map(o => (
                  <SelectItem key={`st:${o.id}`} value={`sous_traitant:${o.id}`}>{o.nom}</SelectItem>
                ))}
              </>
            )}
          </SelectContent>
        </Select>
      </div>
      {/* Pneus */}
      {["pneus_hiver", "pneus_ete"].map(type => (
        <div key={type} className="border border-slate-200 rounded-lg p-3">
          <h4 className="text-sm font-semibold text-slate-700 mb-2">
            {type === "pneus_hiver" ? "Pneus d'hiver" : "Pneus d'été"}
          </h4>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs text-slate-500">Marque</label>
              <Input value={form[type]?.marque || ""} onChange={e => setPneu(type, "marque", e.target.value)} placeholder="Bridgestone" className="mt-0.5 h-8 text-sm" /></div>
            <div><label className="text-xs text-slate-500">Taille</label>
              <Input value={form[type]?.taille || ""} onChange={e => setPneu(type, "taille", e.target.value)} placeholder="225/65R17" className="mt-0.5 h-8 text-sm font-mono" /></div>
            <div><label className="text-xs text-slate-500">Année d'achat</label>
              <Input type="number" value={form[type]?.annee_achat || ""} onChange={e => setPneu(type, "annee_achat", parseInt(e.target.value) || null)} placeholder="2022" className="mt-0.5 h-8 text-sm" /></div>
            <div><label className="text-xs text-slate-500">Condition</label>
              <Select value={form[type]?.condition || "bon"} onValueChange={v => setPneu(type, "condition", v)}>
                <SelectTrigger className="mt-0.5 h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONDITION_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      ))}
      <div><label className="text-xs font-medium text-slate-600">Notes</label>
        <Textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} className="mt-1" /></div>
      {/* Type d'utilisation */}
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1.5">Type d'utilisation</label>
        <div className="flex gap-2 flex-wrap">
          {[
            { val: 'interieur', label: 'Intérieur' },
            { val: 'exterieur', label: 'Extérieur' },
            { val: 'mixte', label: 'Les deux (mixte)' },
          ].map(opt => (
            <button key={opt.val} type="button"
              onClick={() => set("type_utilisation", opt.val)}
              className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${(form.type_utilisation || 'exterieur') === opt.val ? 'bg-indigo-100 border-indigo-400 text-indigo-800' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400'}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      {/* Capacités de remorque */}
      <div className="border border-slate-200 rounded-lg p-3 space-y-2">
        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Capacités de remorque (validation IA)</p>
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input type="checkbox" checked={form.peut_tirer_nacelle || false} onChange={e => set("peut_tirer_nacelle", e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-indigo-600" data-testid="peut-tirer-nacelle" />
          Peut tirer une nacelle
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input type="checkbox" checked={form.peut_tirer_remorque_echafauds || false} onChange={e => set("peut_tirer_remorque_echafauds", e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-indigo-600" data-testid="peut-tirer-echafauds" />
          Peut tirer la remorque d'échafauds
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input type="checkbox" checked={form.utilise_pour_lavage || false} onChange={e => set("utilise_pour_lavage", e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-indigo-600" data-testid="utilise-pour-lavage" />
          Utilisé pour les lavages à pression
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input type="checkbox" checked={form.tire_une_chose_a_la_fois || false} onChange={e => set("tire_une_chose_a_la_fois", e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-amber-600" data-testid="tire-une-chose" />
          <span>Ne peut tirer <strong>qu'une chose à la fois</strong> <span className="text-slate-400 font-normal">(ex: GMC Sierra avec remorque outils)</span></span>
        </label>
        <div className="pt-1">
          <label className="text-xs text-slate-500 block mb-1">Contraintes supplémentaires (texte libre pour l'IA)</label>
          <input type="text" value={form.contraintes_ia || ''} onChange={e => set("contraintes_ia", e.target.value)}
            placeholder="Ex: Jour 1 d'une job extérieure = remorque outils prioritaire, nacelle arrive J2"
            className="w-full border rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-300" />
        </div>
      </div>
      {/* 🆕 [Phase 29 — P1 #5] Section Normes assignées à cette unité */}
      {initial?.id && <NormesUniteSection uniteId={initial.id} api={API} />}

      <div className="flex gap-2 justify-end pt-2">
        <Button variant="outline" onClick={onCancel}>Annuler</Button>
        <Button onClick={() => onSave(form)} className="bg-[#1a237e] hover:bg-[#1a237e]/90 text-white">
          <Save className="w-4 h-4 mr-1" /> Enregistrer
        </Button>
      </div>
    </div>
  );
}

/**
 * 🆕 [Phase 29 — P1 #5] + [Phase 30 — Fix UX Normes côté unité]
 *
 * Section Normes complète sur la fiche unité — création / édition / retrait directement.
 * Réutilise les endpoints `/api/normes-materiel` (GET / POST / PUT / DELETE).
 *
 * Cas gérés à la création :
 *   • Article jamais normé → POST création nouvelle norme {article_id, quantite, unite_ids:[unite]}.
 *   • Article déjà normé pour CETTE quantité → PUT étend `unite_ids` (réutilise norme existante).
 *   • Article déjà normé pour une AUTRE quantité → POST création nouvelle norme distincte
 *     (sauf si l'unité y est déjà : backend renvoie 409, signalé à l'utilisateur).
 *
 * Cas gérés à la modification :
 *   • Si la norme couvre PLUSIEURS unités → afficher avertissement (modif s'applique à toutes).
 *
 * Cas gérés au retrait :
 *   • Norme couvre uniquement cette unité → DELETE complète.
 *   • Norme couvre plusieurs unités → PUT avec `unite_ids` filtré.
 */
function NormesUniteSection({ uniteId, api }) {
  const [normes, setNormes] = useState([]);
  const [articles, setArticles] = useState({});
  const [loading, setLoading] = useState(true);
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNorme, setEditingNorme] = useState(null); // null = create, else = edit existing
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${api}/api/normes-materiel`, { params: { unite_id: uniteId } });
      const ns = r.data?.normes || [];
      setNormes(ns);
      const articleIds = [...new Set(ns.map(n => n.article_id).filter(Boolean))];
      if (articleIds.length > 0) {
        const map = {};
        await Promise.all(articleIds.map(async (aid) => {
          try {
            const a = await axios.get(`${api}/api/inventory/products/${aid}`);
            map[aid] = a.data?.name || aid;
          } catch { map[aid] = aid; }
        }));
        setArticles(map);
      }
    } catch (e) {
      console.error('Erreur load normes unite:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniteId]);

  const openCreate = () => { setEditingNorme(null); setDialogOpen(true); };
  const openEdit = (n) => { setEditingNorme(n); setDialogOpen(true); };

  const handleRetrait = async (n) => {
    const partagee = (n.unite_ids || []).filter(u => u !== uniteId);
    const msg = partagee.length > 0
      ? `Retirer cette unité de la norme « ${n.nom || articles[n.article_id] || 'sans nom'} » ? La norme restera active sur ${partagee.length} autre(s) unité(s).`
      : `Cette norme n'est plus utilisée par aucune autre unité — elle sera complètement supprimée. Continuer ?`;
    if (!window.confirm(msg)) return;
    try {
      if (partagee.length === 0) {
        await axios.delete(`${api}/api/normes-materiel/${n.id}`);
      } else {
        await axios.put(`${api}/api/normes-materiel/${n.id}`, { unite_ids: partagee });
      }
      toast.success('Norme retirée de cette unité.');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors du retrait.');
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="normes-unite-section">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
          Normes assignées à cette unité
        </p>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400" data-testid="normes-unite-count">
            {loading ? '…' : `${normes.length} norme${normes.length > 1 ? 's' : ''}`}
          </span>
          <button
            type="button"
            onClick={openCreate}
            className="text-[11px] px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-medium hidden"
            data-testid="btn-add-norme-unite"
            title="Déplacé vers le bouton top-level « 📋 Normes de matériel » (Phase 35)"
          >
            + Ajouter une norme
          </button>
        </div>
      </div>
      {loading ? (
        <p className="text-xs text-slate-400 italic">Chargement…</p>
      ) : normes.length === 0 ? (
        <div
          className="text-xs text-slate-500 italic p-3 bg-slate-50 border border-dashed border-slate-300 rounded text-center"
          data-testid="normes-unite-empty"
        >
          Aucune norme assignée à cette unité.
          <br />
          <button
            type="button"
            onClick={openCreate}
            className="mt-2 inline-block text-emerald-700 hover:text-emerald-800 underline font-medium not-italic"
            data-testid="btn-add-norme-unite-empty"
          >
            + Ajouter votre première norme
          </button>
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="normes-unite-list">
          {normes.map(n => {
            const nbAutresUnites = (n.unite_ids || []).filter(u => u !== uniteId).length;
            return (
              <li
                key={n.id}
                className="flex items-center justify-between gap-2 p-2 bg-slate-50 border border-slate-200 rounded text-xs"
                data-testid={`normes-unite-item-${n.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">{n.nom || articles[n.article_id] || 'Norme sans nom'}</p>
                  <p className="text-[11px] text-slate-500">
                    Quantité : <strong>{n.quantite ?? '?'}</strong>
                    {n.article_id && (
                      <> · Article : <span className="text-blue-700">{articles[n.article_id] || n.article_id}</span></>
                    )}
                    {nbAutresUnites > 0 && (
                      <> · <span className="text-amber-700">partagée avec {nbAutresUnites} autre(s) unité(s)</span></>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => openEdit(n)}
                    className="text-[11px] px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded font-medium"
                    data-testid={`normes-unite-edit-${n.id}`}
                    title="Modifier la quantité ou le nom"
                  >
                    ✏️ Modifier
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRetrait(n)}
                    className="text-[11px] px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded font-medium"
                    data-testid={`normes-unite-remove-${n.id}`}
                    title="Retirer de cette unité"
                  >
                    🗑️ Retirer
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {dialogOpen && (
        <NormeUniteDialog
          api={api}
          uniteId={uniteId}
          editingNorme={editingNorme}
          existingNormes={normes}
          onClose={() => setDialogOpen(false)}
          onSaved={() => { setDialogOpen(false); reload(); }}
          saving={saving}
          setSaving={setSaving}
        />
      )}
    </div>
  );
}

// 🆕 [Phase 30 — Fix UX Normes] Dialog création/édition norme depuis fiche unité
function NormeUniteDialog({ api, uniteId, editingNorme, existingNormes, onClose, onSaved, saving, setSaving }) {
  const [articles, setArticles] = useState([]);
  const [articleSearch, setArticleSearch] = useState('');
  const [selectedArticleId, setSelectedArticleId] = useState(editingNorme?.article_id || '');
  const [quantite, setQuantite] = useState(editingNorme?.quantite ?? 1);
  const [nom, setNom] = useState(editingNorme?.nom || '');
  const [articlesLoading, setArticlesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${api}/api/inventory/products`);
        if (!cancelled) setArticles(r.data?.products || r.data || []);
      } catch (e) {
        console.error('Erreur load articles:', e);
      } finally {
        if (!cancelled) setArticlesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const filteredArticles = articleSearch.trim()
    ? articles.filter(a => (a.name || a.nom || '').toLowerCase().includes(articleSearch.trim().toLowerCase()))
    : articles.slice(0, 50);

  const isEdit = !!editingNorme;
  const nbAutresUnites = isEdit
    ? (editingNorme.unite_ids || []).filter(u => u !== uniteId).length
    : 0;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!selectedArticleId) { toast.error('Sélectionnez un article.'); return; }
    if (!quantite || Number(quantite) <= 0) { toast.error('Quantité invalide.'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        // PUT existante (quantité / nom — pas unite_ids ici)
        const patch = { quantite: Number(quantite) };
        if (nom !== editingNorme.nom) patch.nom = nom || null;
        await axios.put(`${api}/api/normes-materiel/${editingNorme.id}`, patch);
        toast.success(nbAutresUnites > 0
          ? `Norme modifiée — appliquée aux ${nbAutresUnites + 1} unités partagées.`
          : 'Norme modifiée.');
      } else {
        // Création : chercher norme existante pour ce article_id avec MÊME quantité
        const r = await axios.get(`${api}/api/normes-materiel`, { params: { article_id: selectedArticleId } });
        const candidates = (r.data?.normes || []).filter(n => Number(n.quantite) === Number(quantite));
        const compatible = candidates.find(n => !(n.unite_ids || []).includes(uniteId));
        if (compatible) {
          // Étendre la norme existante
          const newIds = [...new Set([...(compatible.unite_ids || []), uniteId])];
          await axios.put(`${api}/api/normes-materiel/${compatible.id}`, { unite_ids: newIds });
          toast.success('Unité ajoutée à la norme existante (mêmes quantité + article).');
        } else {
          // Création neuve
          await axios.post(`${api}/api/normes-materiel`, {
            article_id: selectedArticleId,
            quantite: Number(quantite),
            unite_ids: [uniteId],
            nom: nom || null,
          });
          toast.success('Norme créée pour cette unité.');
        }
      }
      onSaved();
    } catch (e) {
      const det = e?.response?.data?.detail;
      const msg = typeof det === 'string' ? det : (det?.message || 'Erreur lors de la sauvegarde.');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="norme-unite-dialog-overlay"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="norme-unite-dialog"
      >
        <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
          <h3 className="text-sm font-bold">{isEdit ? '✏️ Modifier la norme' : '➕ Ajouter une norme à cette unité'}</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-700" data-testid="btn-cancel-norme-unite">✕</button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          {isEdit && nbAutresUnites > 0 && (
            <div className="p-2 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800" data-testid="norme-unite-warn-partage">
              ⚠️ Cette norme est partagée avec <strong>{nbAutresUnites} autre(s) unité(s)</strong>.
              Modifier la quantité ici l'appliquera à toutes.
            </div>
          )}
          {!isEdit && (
            <>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Article *</span>
                <input
                  type="text"
                  value={articleSearch}
                  onChange={(e) => setArticleSearch(e.target.value)}
                  placeholder="Rechercher un article…"
                  className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
                  data-testid="input-search-article-norme"
                />
                <div className="mt-1 max-h-40 overflow-y-auto border border-slate-200 rounded bg-white">
                  {articlesLoading ? (
                    <p className="p-2 text-xs text-slate-400 italic">Chargement…</p>
                  ) : filteredArticles.length === 0 ? (
                    <p className="p-2 text-xs text-slate-400 italic">Aucun article trouvé.</p>
                  ) : (
                    filteredArticles.map(a => (
                      <button
                        type="button"
                        key={a.id}
                        onClick={() => { setSelectedArticleId(a.id); setArticleSearch(a.name || a.nom || ''); }}
                        className={`block w-full text-left px-2 py-1 hover:bg-blue-50 text-xs ${selectedArticleId === a.id ? 'bg-blue-100 font-medium' : ''}`}
                        data-testid={`option-article-${a.id}`}
                      >
                        {a.name || a.nom || a.id}
                      </button>
                    ))
                  )}
                </div>
              </label>
            </>
          )}
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Quantité *</span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={quantite}
              onChange={(e) => setQuantite(e.target.value)}
              className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
              data-testid="input-quantite-norme"
              required
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Nom (optionnel)</span>
            <input
              type="text"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              placeholder="Par défaut : nom de l'article"
              className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
              data-testid="input-nom-norme"
            />
          </label>
        </div>
        <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs bg-white border border-slate-300 rounded hover:bg-slate-50"
            data-testid="btn-cancel-norme-unite-bottom"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50 font-medium"
            data-testid="btn-save-norme-unite"
          >
            {saving ? 'Enregistrement…' : (isEdit ? 'Modifier' : 'Enregistrer')}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Inspections nacelles/équipements (Phase 3) ──────────────────────────────
function InspectionNacelleSection({ equipement, canEdit }) {
  const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
  const { user } = useAuth();
  const [inspections, setInspections] = useState([]);
  const [template, setTemplate] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [notesGen, setNotesGen] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [insRes, tmplRes] = await Promise.all([
        axios.get(`${API_URL}/api/equipements-inventaire/${equipement.id}/inspections?limit=20`),
        axios.get(`${API_URL}/api/equipements-inventaire/${equipement.id}/inspection-template`),
      ]);
      setInspections(insRes.data?.inspections || []);
      setTemplate(tmplRes.data?.template || []);
    } catch (e) {
      console.error('Erreur chargement inspections:', e);
    }
    setLoading(false);
  };

  useEffect(() => { reload(); }, [equipement.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const startNewInspection = () => {
    setItems(template.map(t => ({ id: t.id, label: t.label, checked: true, notes: '' })));
    setNotesGen('');
    setPhotos([]);
    setShowForm(true);
  };

  const toggleItem = (id) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, checked: !i.checked } : i));
  };
  const updateItemNotes = (id, notes) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, notes } : i));
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    try {
      setUploadingPhoto(true);
      const { compressImage } = await import('@/lib/imageCompression');
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append('file', compressed);
      const res = await axios.post(`${API_URL}/api/equipements-inventaire/upload-photo`, fd);
      if (res.data?.photo_url) {
        setPhotos(prev => [...prev, res.data.photo_url]);
      }
    } catch (e) {
      console.error('Erreur upload photo:', e);
      toast.error('Erreur upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const conforme = items.every(i => i.checked);
      await axios.post(`${API_URL}/api/equipements-inventaire/${equipement.id}/inspections`, {
        equipement_id: equipement.id,
        inspecteur_nom: user ? `${user.prenom || ''} ${user.nom || ''}`.trim() : 'Inspecteur',
        items,
        notes_generales: notesGen,
        photos,
        conforme,
      });
      toast.success(conforme ? 'Inspection conforme enregistrée' : 'Inspection NON conforme enregistrée');
      setShowForm(false);
      await reload();
    } catch (e) {
      console.error(e);
      toast.error('Erreur enregistrement inspection');
    }
    setSaving(false);
  };

  const handleDelete = async (insId) => {
    if (!window.confirm('Supprimer cette inspection ?')) return;
    try {
      await axios.delete(`${API_URL}/api/equipements-inventaire/${equipement.id}/inspections/${insId}`);
      toast.success('Inspection supprimée');
      await reload();
    } catch { toast.error('Erreur suppression'); }
  };

  const derniereInspection = inspections[0];
  const joursDepuisDerniere = derniereInspection
    ? Math.floor((Date.now() - new Date(derniereInspection.date_inspection).getTime()) / 86400000)
    : null;

  return (
    <div className="mt-3 pt-3 border-t border-slate-200" data-testid={`inspection-section-${equipement.id}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-bold text-slate-700 uppercase flex items-center gap-1.5">
          <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
          Inspections {equipement.est_nacelle ? 'nacelle' : ''}
        </p>
        {canEdit && !showForm && (
          <Button
            size="sm"
            onClick={startNewInspection}
            className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`btn-nouvelle-inspection-${equipement.id}`}
          >
            <Plus className="w-3 h-3 mr-1" /> Nouvelle inspection
          </Button>
        )}
      </div>

      {derniereInspection && !showForm && (
        <div className={`rounded-lg p-2 mb-2 border text-xs ${derniereInspection.conforme ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-center justify-between">
            <span className={`font-semibold ${derniereInspection.conforme ? 'text-emerald-800' : 'text-red-800'}`}>
              {derniereInspection.conforme ? '✓ Conforme' : '⚠️ Non conforme'}
            </span>
            <span className="text-slate-500">
              il y a {joursDepuisDerniere}j — par {derniereInspection.inspecteur_nom || 'Inconnu'}
            </span>
          </div>
        </div>
      )}

      {showForm && (
        <div className="bg-white border-2 border-emerald-200 rounded-lg p-3 space-y-2">
          <div className="max-h-64 overflow-y-auto space-y-1.5">
            {items.map(it => (
              <div key={it.id} className="flex items-start gap-2 p-1.5 rounded hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={it.checked}
                  onChange={() => toggleItem(it.id)}
                  className="mt-1 w-4 h-4 rounded border-slate-300 text-emerald-600"
                  data-testid={`inspection-item-${it.id}`}
                />
                <div className="flex-1 min-w-0">
                  <label className={`text-xs font-medium cursor-pointer ${it.checked ? 'text-slate-700' : 'text-red-700'}`} onClick={() => toggleItem(it.id)}>
                    {it.label} {!it.checked && <span className="text-[10px] bg-red-100 text-red-700 px-1 rounded">NON CONFORME</span>}
                  </label>
                  {!it.checked && (
                    <Input
                      value={it.notes || ''}
                      onChange={e => updateItemNotes(it.id, e.target.value)}
                      placeholder="Décrire le problème..."
                      className="mt-1 h-7 text-xs"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
          <Textarea
            value={notesGen}
            onChange={e => setNotesGen(e.target.value)}
            placeholder="Notes générales (optionnel)"
            rows={2}
            className="text-xs"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <label className="cursor-pointer inline-flex items-center gap-1 text-xs bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded">
              <Camera className="w-3 h-3" /> {uploadingPhoto ? '…' : 'Ajouter photo'}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value=''; }}
              />
            </label>
            {photos.map((p, i) => (
              <div key={i} className="relative">
                <img src={p} alt="" className="w-12 h-12 object-cover rounded border" />
                <button
                  onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]"
                >×</button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)} className="h-7 text-xs">Annuler</Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid={`btn-save-inspection-${equipement.id}`}
            >
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      )}

      {!showForm && (
        <div className="space-y-1">
          {loading ? (
            <p className="text-xs text-slate-400 italic">Chargement…</p>
          ) : inspections.length === 0 ? (
            <p className="text-xs text-slate-400 italic">Aucune inspection enregistrée</p>
          ) : (
            inspections.slice(0, 5).map(ins => {
              const nonConf = (ins.items || []).filter(i => !i.checked);
              return (
                <div key={ins.id} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1.5">
                  <div className="min-w-0">
                    <span className={`font-semibold ${ins.conforme ? 'text-emerald-700' : 'text-red-700'}`}>
                      {ins.conforme ? '✓' : '⚠️'}
                    </span>
                    <span className="ml-1 text-slate-600">
                      {formatLocalDate(ins.date_inspection)} — {ins.inspecteur_nom || 'Inspecteur'}
                    </span>
                    {nonConf.length > 0 && <span className="ml-1 text-[10px] text-red-600">({nonConf.length} problème{nonConf.length > 1 ? 's' : ''})</span>}
                    {(ins.photos || []).length > 0 && <span className="ml-1 text-[10px] text-slate-400">📷 {ins.photos.length}</span>}
                  </div>
                  {canEdit && (
                    <button onClick={() => handleDelete(ins.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ── Éditeur du template global d'inspection (2026-04-27) ────────────────────
function TemplateInspectionGlobalEditor({ canEdit }) {
  const API_URL = API;
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [isDefault, setIsDefault] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/inspection-template-global`);
      setItems(res.data?.items || []);
      setIsDefault(!!res.data?.is_default);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/inspection-template-global`, { items: items.filter(i => (i.label || '').trim()) });
      toast.success('Template global mis à jour');
      setIsDefault(false);
      setOpen(false);
    } catch (e) {
      console.error(e);
      toast.error('Erreur sauvegarde');
    }
    setSaving(false);
  };

  return (
    <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-3 mb-3" data-testid="editeur-template-global">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="min-w-0">
          <p className="text-sm font-bold text-amber-900 flex items-center gap-1.5">
            🛡️ Template d'inspection global {isDefault && <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full font-normal">Défaut</span>}
          </p>
          <p className="text-[11px] text-amber-700 mt-0.5">
            Critères communs à toutes les nacelles. Chaque nacelle peut avoir des critères additionnels propres (dans son édition).
          </p>
        </div>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            className="text-xs h-7 border-amber-400"
            onClick={() => setOpen(!open)}
            data-testid="btn-edit-template-global"
          >
            {open ? 'Fermer' : '⚙️ Configurer le template'}
          </Button>
        )}
      </div>

      {open && (
        <div className="mt-3 bg-white border border-amber-200 rounded-lg p-3 space-y-2">
          <p className="text-xs text-slate-500 italic">
            Ces critères apparaîtront dans toutes les inspections de nacelle (avant location, après location, routine).
          </p>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {items.map((it, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-slate-400 w-5 text-right">{i + 1}.</span>
                <Input
                  value={it.label || ''}
                  onChange={e => {
                    const arr = [...items]; arr[i] = { ...arr[i], label: e.target.value }; setItems(arr);
                  }}
                  placeholder="Ex: Système hydraulique"
                  className="h-8 text-xs flex-1"
                />
                <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400"
                  onClick={() => setItems(items.filter((_, idx) => idx !== i))}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
          <Button
            type="button" size="sm" variant="outline"
            className="h-7 text-xs border-dashed border-amber-300 text-amber-700"
            onClick={() => setItems([...items, { label: '' }])}
          >
            <Plus className="w-3 h-3 mr-1" /> Ajouter un critère global
          </Button>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button size="sm" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button size="sm" onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="btn-save-template-global">
              {saving ? 'Enregistrement…' : 'Sauvegarder'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Onglet Unités d'élévation (équipements inventaire) — STACKED CARDS ────────
const EMOJIS_EQ = [
  { value: '\u{1F3D7}\u{FE0F}', label: 'Nacelle' },
  { value: '\u{1FA9C}', label: 'Échafaud' },
  { value: '\u{1F69B}', label: 'Camion' },
  { value: '\u{1F527}', label: 'Outil' },
];

const EMPTY_LIVRAISON = {
  km_0_55: 0, km_55_80: 0, km_80_plus: 0,                    // aller seulement (rétrocompat)
  km_0_55_ar: 0, km_55_80_ar: 0, km_80_plus_ar: 0,           // ✅ 2026-04-27 : aller-retour
  prix_livraison_precise: 0, prix_livraison_adresse: 0,
};

function OngletElevation({ canEdit, showCreate, onCloseCreate }) {
  const API = process.env.REACT_APP_BACKEND_URL || '';
  const [equipements, setEquipements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [editEq, setEditEq] = useState(null);
  const [form, setForm] = useState({
    nom: '', annee: '', hauteur_travail: '', nb_etages: '', emoji: '\u{1F3D7}\u{FE0F}',
    est_nacelle: false, prix_jour: 0, prix_semaine: 0, prix_mois: 0,
    numero_unite: '', // 🆕 # d'unité
    plaque: '', numero_serie: '', assurance_expiration: '',
    livraison: { ...EMPTY_LIVRAISON }, notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setLiv = (k, v) => setForm(f => ({ ...f, livraison: { ...f.livraison, [k]: v } }));
  const setEd = (k, v) => setEditEq(f => f ? { ...f, [k]: v } : f);
  const setEdLiv = (k, v) => setEditEq(f => f ? { ...f, livraison: { ...(f.livraison || EMPTY_LIVRAISON), [k]: v } } : f);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/equipements-inventaire`);
      setEquipements((res.data?.equipements || []).filter(e => e.actif !== false));
    } catch (e) {
      console.error('Erreur chargement équipements:', e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => setForm({
    nom: '', annee: '', hauteur_travail: '', nb_etages: '', emoji: '\u{1F3D7}\u{FE0F}',
    est_nacelle: false, prix_jour: 0, prix_semaine: 0, prix_mois: 0,
    numero_unite: '', // 🆕
    plaque: '', numero_serie: '', assurance_expiration: '',
    livraison: { ...EMPTY_LIVRAISON }, notes: '',
  });

  const handleSave = async () => {
    if (!form.nom.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/equipements-inventaire`, {
        nom: form.nom, annee: form.annee ? parseInt(form.annee) : null,
        hauteur_travail: form.hauteur_travail || null, nb_etages: form.nb_etages ? parseInt(form.nb_etages) : null,
        emoji: form.emoji, est_nacelle: form.est_nacelle,
        prix_jour: form.prix_jour, prix_semaine: form.prix_semaine, prix_mois: form.prix_mois,
        numero_unite: form.numero_unite || '', // 🆕
        plaque: form.plaque || '',
        numero_serie: form.numero_serie || '',
        assurance_expiration: form.assurance_expiration || null,
        livraison: form.livraison, notes: form.notes || null,
      });
      resetForm();
      await load();
      toast.success('Équipement ajouté');
      onCloseCreate(false);
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const handleUpdate = async () => {
    if (!editEq?.id) return;
    try {
      await axios.put(`${API}/api/equipements-inventaire/${editEq.id}`, {
        nom: editEq.nom, annee: editEq.annee ? parseInt(editEq.annee) : null,
        hauteur_travail: editEq.hauteur_travail || null, nb_etages: editEq.nb_etages ? parseInt(editEq.nb_etages) : null,
        emoji: editEq.emoji || '', est_nacelle: editEq.est_nacelle || false,
        prix_jour: editEq.prix_jour || 0, prix_semaine: editEq.prix_semaine || 0, prix_mois: editEq.prix_mois || 0,
        numero_unite: editEq.numero_unite || '', // 🆕
        plaque: editEq.plaque || '',
        numero_serie: editEq.numero_serie || '',
        assurance_expiration: editEq.assurance_expiration || null,
        livraison: editEq.livraison || EMPTY_LIVRAISON, notes: editEq.notes || null,
      });
      // ✅ 2026-04-27 : sauvegarder les critères d'inspection additionnels (séparé)
      if (editEq.est_nacelle) {
        await axios.put(`${API}/api/equipements-inventaire/${editEq.id}/inspection-extras`, {
          inspection_extras: editEq.inspection_extras || [],
        });
      }
      toast.success('Équipement mis à jour');
      setEditEq(null);
      await load();
    } catch { toast.error('Erreur mise à jour'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet équipement ?')) return;
    try {
      await axios.delete(`${API}/api/equipements-inventaire/${id}`);
      toast.success('Équipement supprimé');
      if (expandedId === id) setExpandedId(null);
      await load();
    } catch { toast.error('Erreur suppression'); }
  };

  const toggleExpand = (id) => {
    setExpandedId(prev => prev === id ? null : id);
    if (editEq?.id !== id) setEditEq(null);
  };

  const nacKpi = {
    total: equipements.length,
    nacelles: equipements.filter(n => n.est_nacelle).length,
    avecTarif: equipements.filter(n => (n.prix_jour||0)>0||(n.prix_semaine||0)>0||(n.prix_mois||0)>0).length,
  };

  const hasLivraison = (liv) => liv && ((liv.km_0_55||0) > 0 || (liv.km_55_80||0) > 0 || (liv.km_80_plus||0) > 0 || (liv.prix_livraison_precise||0) > 0 || (liv.prix_livraison_adresse||0) > 0);

  return (
    <div className="space-y-3 mt-4">
      {!loading && equipements.length > 0 && (
        <div className="grid grid-cols-3 gap-2" data-testid="kpi-elevation">
          <div className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-lg px-3 py-2.5">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0"><Wrench className="w-4 h-4 text-amber-700" /></div>
            <div><p className="text-lg font-bold text-slate-900 leading-none">{nacKpi.total}</p><p className="text-[10px] text-slate-500 uppercase font-medium mt-0.5">Équipements</p></div>
          </div>
          <div className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-lg px-3 py-2.5">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${nacKpi.nacelles > 0 ? 'bg-cyan-100' : 'bg-slate-100'}`}>
              <span className="text-sm">{'\u{1F3D7}\u{FE0F}'}</span>
            </div>
            <div><p className={`text-lg font-bold leading-none ${nacKpi.nacelles > 0 ? 'text-cyan-700' : 'text-slate-400'}`}>{nacKpi.nacelles}</p><p className="text-[10px] text-slate-500 uppercase font-medium mt-0.5">Nacelles</p></div>
          </div>
          <div className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-lg px-3 py-2.5">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${nacKpi.avecTarif > 0 ? 'bg-emerald-100' : 'bg-slate-100'}`}>
              <Hash className={`w-4 h-4 ${nacKpi.avecTarif > 0 ? 'text-emerald-600' : 'text-slate-400'}`} />
            </div>
            <div><p className={`text-lg font-bold leading-none ${nacKpi.avecTarif > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>{nacKpi.avecTarif}</p><p className="text-[10px] text-slate-500 uppercase font-medium mt-0.5">Avec tarif</p></div>
          </div>
        </div>
      )}

      {/* ✅ 2026-04-27 : Éditeur du template global d'inspection */}
      <TemplateInspectionGlobalEditor canEdit={canEdit} />

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600" /></div>
      ) : equipements.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-xl" data-testid="elevation-empty">
          <Wrench className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-sm text-slate-500 font-medium">Aucun équipement enregistré</p>
          <p className="text-xs text-slate-400 mt-1">Cliquez sur "Ajouter unité" pour créer votre premier équipement</p>
        </div>
      ) : equipements.map(n => {
        const isExpanded = expandedId === n.id;
        const isEditing = editEq?.id === n.id;
        const liv = n.livraison || {};
        return (
          <div key={n.id} className={`${CARD_BASE} ${isExpanded ? 'border-amber-300 shadow-md ring-1 ring-amber-100' : CARD_IDLE}`} data-testid={`nacelle-card-${n.id}`}>
            <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => toggleExpand(n.id)}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`${ICON_BOX} bg-amber-50 border border-amber-100`}>
                  <span className="text-xl">{n.emoji || '\u{1F3D7}\u{FE0F}'}</span>
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-800 truncate">
                    {n.numero_unite && <span className="text-indigo-600 font-mono text-sm">{n.numero_unite} — </span>}
                    {n.nom}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {[n.annee, n.hauteur_travail].filter(Boolean).join(' · ')}
                  </p>
                  <div className="flex gap-1.5 mt-1 flex-wrap">
                    {n.est_nacelle && <span className="text-[10px] bg-cyan-50 text-cyan-700 border border-cyan-200 px-1.5 py-0.5 rounded-full font-medium">Nacelle</span>}
                    {n.hauteur_travail && <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full font-medium">{n.hauteur_travail}</span>}
                    {n.nb_etages > 0 && <span className="text-[10px] bg-violet-50 text-violet-700 border border-violet-200 px-1.5 py-0.5 rounded-full font-medium">{n.nb_etages} étages</span>}
                    {(n.prix_jour > 0 || n.prix_semaine > 0) && (
                      <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full font-medium">
                        {n.prix_jour > 0 ? `${n.prix_jour}$/j` : `${n.prix_semaine}$/sem`}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-2">
                {canEdit && (
                  <>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-amber-600"
                      onClick={e => { e.stopPropagation(); setEditEq({ ...n, livraison: n.livraison || { ...EMPTY_LIVRAISON } }); setExpandedId(n.id); }} data-testid={`edit-nacelle-${n.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                      onClick={e => { e.stopPropagation(); handleDelete(n.id); }} data-testid={`delete-nacelle-${n.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
              </div>
            </div>
            {isExpanded && (
              <div className="border-t border-slate-100 p-4 bg-slate-50/50">
                {isEditing ? (
                  <EquipementEditForm eq={editEq} setEd={setEd} setEdLiv={setEdLiv} onCancel={() => setEditEq(null)} onSave={handleUpdate} />
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2">
                      {n.annee && <div><p className="text-[10px] text-slate-400 uppercase font-semibold">Année</p><p className="text-sm font-medium text-slate-800">{n.annee}</p></div>}
                      {n.hauteur_travail && <div><p className="text-[10px] text-slate-400 uppercase font-semibold">Hauteur</p><p className="text-sm font-medium text-slate-800">{n.hauteur_travail}</p></div>}
                      {n.nb_etages > 0 && <div><p className="text-[10px] text-slate-400 uppercase font-semibold">Étages</p><p className="text-sm font-medium text-slate-800">{n.nb_etages}</p></div>}
                    </div>
                    {(n.prix_jour > 0 || n.prix_semaine > 0 || n.prix_mois > 0) && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <p className="text-[10px] text-emerald-600 uppercase font-semibold mb-1">Prix de location</p>
                        <div className="flex gap-6">
                          {n.prix_jour > 0 && <span className="text-sm font-semibold text-emerald-800">{n.prix_jour}$ <span className="text-xs font-normal text-emerald-600">/jour</span></span>}
                          {n.prix_semaine > 0 && <span className="text-sm font-semibold text-emerald-800">{n.prix_semaine}$ <span className="text-xs font-normal text-emerald-600">/sem.</span></span>}
                          {n.prix_mois > 0 && <span className="text-sm font-semibold text-emerald-800">{n.prix_mois}$ <span className="text-xs font-normal text-emerald-600">/mois</span></span>}
                        </div>
                      </div>
                    )}
                    {hasLivraison(liv) && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-[10px] text-blue-600 uppercase font-semibold mb-1">Prix de livraison</p>
                        <div className="flex flex-wrap gap-4">
                          {(liv.km_0_55||0) > 0 && <span className="text-sm font-semibold text-blue-800">{liv.km_0_55}$ <span className="text-xs font-normal text-blue-600">0-55 km</span></span>}
                          {(liv.km_55_80||0) > 0 && <span className="text-sm font-semibold text-blue-800">{liv.km_55_80}$ <span className="text-xs font-normal text-blue-600">55-80 km</span></span>}
                          {(liv.km_80_plus||0) > 0 && <span className="text-sm font-semibold text-blue-800">{liv.km_80_plus}$ <span className="text-xs font-normal text-blue-600">80 km+</span></span>}
                          {(liv.prix_livraison_precise||0) > 0 && <span className="text-sm font-semibold text-blue-800">{liv.prix_livraison_precise}$ <span className="text-xs font-normal text-blue-600">endroit précis</span></span>}
                          {(liv.prix_livraison_adresse||0) > 0 && <span className="text-sm font-semibold text-blue-800">{liv.prix_livraison_adresse}$ <span className="text-xs font-normal text-blue-600">devant adresse</span></span>}
                        </div>
                      </div>
                    )}
                    {n.notes && <div><p className="text-[10px] text-slate-400 uppercase font-semibold">Notes</p><p className="text-sm text-slate-600 mt-0.5">{n.notes}</p></div>}
                    <InspectionNacelleSection equipement={n} canEdit={canEdit} />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={onCloseCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Ajouter un équipement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2"><Label>Nom de l'équipement *</Label><Input value={form.nom} onChange={e => set('nom', e.target.value)} placeholder="Ex: Nacelle JLG 450AJ" className="mt-1" data-testid="input-nacelle-nom" /></div>
              <div>
                <Label>Icône</Label>
                <div className="flex gap-1.5 mt-1">
                  {EMOJIS_EQ.map(em => (
                    <button key={em.value} type="button" onClick={() => set('emoji', em.value)} title={em.label}
                      className={`text-xl p-1.5 rounded-lg border-2 transition-all ${form.emoji === em.value ? 'border-blue-500 bg-blue-50 scale-110' : 'border-gray-200 hover:border-gray-400'}`}>
                      {em.value}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div><Label>Année</Label><Input type="number" value={form.annee} onChange={e => set('annee', e.target.value)} placeholder="Ex: 2025" className="mt-1" /></div>
              <div><Label>Hauteur de travail</Label><Input value={form.hauteur_travail} onChange={e => set('hauteur_travail', e.target.value)} placeholder="Ex: 55'" className="mt-1" /></div>
              <div><Label>Nombre d'étages (échafauds)</Label><Input type="number" min="0" value={form.nb_etages} onChange={e => set('nb_etages', e.target.value)} placeholder="Ex: 5" className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div><Label># d'unité</Label><Input value={form.numero_unite} onChange={e => set('numero_unite', e.target.value)} placeholder="U-001" className="mt-1 font-mono" /></div>
              <div><Label># Plaque</Label><Input value={form.plaque} onChange={e => set('plaque', e.target.value.toUpperCase())} placeholder="ABC-1234" className="mt-1" /></div>
              <div><Label># Série</Label><Input value={form.numero_serie} onChange={e => set('numero_serie', e.target.value)} placeholder="SN123456" className="mt-1" /></div>
              <div><Label>Assurance (exp.)</Label><Input type="date" value={form.assurance_expiration} onChange={e => set('assurance_expiration', e.target.value)} className="mt-1" /></div>
            </div>
            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-slate-600 mb-2">Prix de location</p>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Par jour ($)</Label><Input type="number" step="0.01" min="0" value={form.prix_jour ?? ''} onChange={e => set('prix_jour', parseFloat(e.target.value) || 0)} className="mt-1" /></div>
                <div><Label className="text-xs">Par semaine ($)</Label><Input type="number" step="0.01" min="0" value={form.prix_semaine ?? ''} onChange={e => set('prix_semaine', parseFloat(e.target.value) || 0)} className="mt-1" /></div>
                <div><Label className="text-xs">Par mois ($)</Label><Input type="number" step="0.01" min="0" value={form.prix_mois ?? ''} onChange={e => set('prix_mois', parseFloat(e.target.value) || 0)} className="mt-1" /></div>
              </div>
            </div>
            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-slate-600 mb-2">Prix de livraison par distance</p>

              {/* Aller seulement */}
              <p className="text-[11px] text-slate-500 font-medium uppercase tracking-wide mb-1">Aller seulement</p>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">0-55 km ($)</Label><Input type="number" step="0.01" min="0" value={form.livraison.km_0_55 ?? ''} onChange={e => setLiv('km_0_55', parseFloat(e.target.value) || 0)} className="mt-1" /></div>
                <div><Label className="text-xs">55-80 km ($)</Label><Input type="number" step="0.01" min="0" value={form.livraison.km_55_80 ?? ''} onChange={e => setLiv('km_55_80', parseFloat(e.target.value) || 0)} className="mt-1" /></div>
                <div><Label className="text-xs">80 km et + ($)</Label><Input type="number" step="0.01" min="0" value={form.livraison.km_80_plus ?? ''} onChange={e => setLiv('km_80_plus', parseFloat(e.target.value) || 0)} className="mt-1" /></div>
              </div>

              {/* ✅ 2026-04-27 : Aller-retour */}
              <p className="text-[11px] text-slate-500 font-medium uppercase tracking-wide mt-3 mb-1">Aller-retour</p>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">0-55 km ($)</Label><Input type="number" step="0.01" min="0" value={form.livraison.km_0_55_ar ?? ''} onChange={e => setLiv('km_0_55_ar', parseFloat(e.target.value) || 0)} className="mt-1" /></div>
                <div><Label className="text-xs">55-80 km ($)</Label><Input type="number" step="0.01" min="0" value={form.livraison.km_55_80_ar ?? ''} onChange={e => setLiv('km_55_80_ar', parseFloat(e.target.value) || 0)} className="mt-1" /></div>
                <div><Label className="text-xs">80 km et + ($)</Label><Input type="number" step="0.01" min="0" value={form.livraison.km_80_plus_ar ?? ''} onChange={e => setLiv('km_80_plus_ar', parseFloat(e.target.value) || 0)} className="mt-1" /></div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <div><Label className="text-xs">Livraison endroit précis ($)</Label><Input type="number" step="0.01" min="0" value={form.livraison.prix_livraison_precise ?? ''} onChange={e => setLiv('prix_livraison_precise', parseFloat(e.target.value) || 0)} className="mt-1" /></div>
                <div><Label className="text-xs">Livraison devant adresse ($)</Label><Input type="number" step="0.01" min="0" value={form.livraison.prix_livraison_adresse ?? ''} onChange={e => setLiv('prix_livraison_adresse', parseFloat(e.target.value) || 0)} className="mt-1" /></div>
              </div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} placeholder="Notes internes..." className="mt-1" /></div>
            <label className="flex items-center gap-3 p-3 bg-cyan-50 border border-cyan-200 rounded-lg cursor-pointer hover:bg-cyan-100 transition-colors">
              <input type="checkbox" checked={form.est_nacelle || false} onChange={e => set('est_nacelle', e.target.checked)}
                className="w-5 h-5 rounded border-cyan-400 text-cyan-600 focus:ring-cyan-400" data-testid="est-nacelle-checkbox" />
              <div>
                <span className="font-semibold text-sm text-cyan-800">{'\u{1F3D7}'} C'est une nacelle</span>
                <p className="text-xs text-cyan-600 mt-0.5">Sera proposée automatiquement lors de la planification des routes de lavage</p>
              </div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onCloseCreate(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="btn-save-nacelle">
              {saving ? 'Enregistrement...' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Formulaire d'édition inline pour équipement ──────────────────────────────
function EquipementEditForm({ eq, setEd, setEdLiv, onCancel, onSave }) {
  const liv = eq.livraison || EMPTY_LIVRAISON;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2"><Label className="text-xs">Nom de l'équipement *</Label>
          <Input value={eq.nom} onChange={e => setEd('nom', e.target.value)} className="mt-1 h-8 text-sm" /></div>
        <div>
          <Label className="text-xs">Icône</Label>
          <div className="flex gap-1.5 mt-1">
            {EMOJIS_EQ.map(em => (
              <button key={em.value} type="button" onClick={() => setEd('emoji', em.value)} title={em.label}
                className={`text-lg p-1 rounded-lg border-2 transition-all ${eq.emoji === em.value ? 'border-blue-500 bg-blue-50 scale-110' : 'border-gray-200 hover:border-gray-400'}`}>
                {em.value}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div><Label className="text-xs">Année</Label>
          <Input type="number" value={eq.annee || ''} onChange={e => setEd('annee', e.target.value)} className="mt-1 h-8 text-sm" /></div>
        <div><Label className="text-xs">Hauteur de travail</Label>
          <Input value={eq.hauteur_travail || ''} onChange={e => setEd('hauteur_travail', e.target.value)} className="mt-1 h-8 text-sm" /></div>
        <div><Label className="text-xs">Nombre d'étages</Label>
          <Input type="number" min="0" value={eq.nb_etages || ''} onChange={e => setEd('nb_etages', e.target.value)} className="mt-1 h-8 text-sm" /></div>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <div><Label className="text-xs"># d'unité</Label>
          <Input value={eq.numero_unite || ''} onChange={e => setEd('numero_unite', e.target.value)} placeholder="U-001" className="mt-1 h-8 text-sm font-mono" /></div>
        <div><Label className="text-xs"># Plaque</Label>
          <Input value={eq.plaque || ''} onChange={e => setEd('plaque', e.target.value.toUpperCase())} placeholder="ABC-1234" className="mt-1 h-8 text-sm" /></div>
        <div><Label className="text-xs"># Série</Label>
          <Input value={eq.numero_serie || ''} onChange={e => setEd('numero_serie', e.target.value)} placeholder="SN123456" className="mt-1 h-8 text-sm" /></div>
        <div><Label className="text-xs">Assurance (exp.)</Label>
          <Input type="date" value={eq.assurance_expiration || ''} onChange={e => setEd('assurance_expiration', e.target.value)} className="mt-1 h-8 text-sm" /></div>
      </div>
      <div className="border-t pt-3">
        <p className="text-xs font-semibold text-slate-600 mb-2">Prix de location</p>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs">Par jour ($)</Label>
            <Input type="number" step="0.01" min="0" value={eq.prix_jour ?? ''} onChange={e => setEd('prix_jour', parseFloat(e.target.value) || 0)} className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs">Par semaine ($)</Label>
            <Input type="number" step="0.01" min="0" value={eq.prix_semaine ?? ''} onChange={e => setEd('prix_semaine', parseFloat(e.target.value) || 0)} className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs">Par mois ($)</Label>
            <Input type="number" step="0.01" min="0" value={eq.prix_mois ?? ''} onChange={e => setEd('prix_mois', parseFloat(e.target.value) || 0)} className="mt-1 h-8 text-sm" /></div>
        </div>
      </div>
      <div className="border-t pt-3">
        <p className="text-xs font-semibold text-slate-600 mb-2">Prix de livraison par distance</p>

        <p className="text-[11px] text-slate-500 font-medium uppercase tracking-wide mb-1">Aller seulement</p>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs">0-55 km ($)</Label>
            <Input type="number" step="0.01" min="0" value={liv.km_0_55 ?? ''} onChange={e => setEdLiv('km_0_55', parseFloat(e.target.value) || 0)} className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs">55-80 km ($)</Label>
            <Input type="number" step="0.01" min="0" value={liv.km_55_80 ?? ''} onChange={e => setEdLiv('km_55_80', parseFloat(e.target.value) || 0)} className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs">80 km et + ($)</Label>
            <Input type="number" step="0.01" min="0" value={liv.km_80_plus ?? ''} onChange={e => setEdLiv('km_80_plus', parseFloat(e.target.value) || 0)} className="mt-1 h-8 text-sm" /></div>
        </div>

        {/* ✅ 2026-04-27 : Aller-retour */}
        <p className="text-[11px] text-slate-500 font-medium uppercase tracking-wide mt-3 mb-1">Aller-retour</p>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs">0-55 km ($)</Label>
            <Input type="number" step="0.01" min="0" value={liv.km_0_55_ar ?? ''} onChange={e => setEdLiv('km_0_55_ar', parseFloat(e.target.value) || 0)} className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs">55-80 km ($)</Label>
            <Input type="number" step="0.01" min="0" value={liv.km_55_80_ar ?? ''} onChange={e => setEdLiv('km_55_80_ar', parseFloat(e.target.value) || 0)} className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs">80 km et + ($)</Label>
            <Input type="number" step="0.01" min="0" value={liv.km_80_plus_ar ?? ''} onChange={e => setEdLiv('km_80_plus_ar', parseFloat(e.target.value) || 0)} className="mt-1 h-8 text-sm" /></div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div><Label className="text-xs">Livraison endroit précis ($)</Label>
            <Input type="number" step="0.01" min="0" value={liv.prix_livraison_precise ?? ''} onChange={e => setEdLiv('prix_livraison_precise', parseFloat(e.target.value) || 0)} className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs">Livraison devant adresse ($)</Label>
            <Input type="number" step="0.01" min="0" value={liv.prix_livraison_adresse ?? ''} onChange={e => setEdLiv('prix_livraison_adresse', parseFloat(e.target.value) || 0)} className="mt-1 h-8 text-sm" /></div>
        </div>
      </div>
      <div><Label className="text-xs">Notes</Label>
        <Textarea value={eq.notes || ''} onChange={e => setEd('notes', e.target.value)} rows={2} placeholder="Notes internes..." className="mt-1 text-sm" /></div>

      {/* ✅ 2026-04-27 : Critères d'inspection ADDITIONNELS propres à cette nacelle */}
      {eq.est_nacelle && (
        <div className="border-t pt-3">
          <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
            🛡️ Critères d'inspection additionnels
            <span className="text-[10px] text-slate-400 font-normal italic">(s'ajoutent au template global)</span>
          </Label>
          <div className="space-y-1.5 mt-2">
            {(eq.inspection_extras || []).map((it, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={it.label || ''}
                  onChange={e => {
                    const arr = [...(eq.inspection_extras || [])];
                    arr[i] = { ...arr[i], label: e.target.value };
                    setEd('inspection_extras', arr);
                  }}
                  placeholder="Ex: Vérifier la plaque tournante"
                  className="h-8 text-xs flex-1"
                />
                <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400"
                  onClick={() => setEd('inspection_extras', (eq.inspection_extras || []).filter((_, idx) => idx !== i))}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
            <Button
              type="button" size="sm" variant="outline"
              className="h-7 text-xs border-dashed border-amber-300 text-amber-700"
              onClick={() => setEd('inspection_extras', [...(eq.inspection_extras || []), { label: '' }])}
              data-testid="btn-add-extra-critere"
            >
              <Plus className="w-3 h-3 mr-1" /> Ajouter un critère propre à cette nacelle
            </Button>
          </div>
        </div>
      )}

      <label className="flex items-center gap-3 p-2.5 bg-cyan-50 border border-cyan-200 rounded-lg cursor-pointer hover:bg-cyan-100 transition-colors">
        <input type="checkbox" checked={eq.est_nacelle || false} onChange={e => setEd('est_nacelle', e.target.checked)}
          className="w-4 h-4 rounded border-cyan-400 text-cyan-600 focus:ring-cyan-400" />
        <div>
          <span className="font-semibold text-xs text-cyan-800">{'\u{1F3D7}'} C'est une nacelle</span>
          <p className="text-[10px] text-cyan-600">Sera proposée lors de la planification des routes de lavage</p>
        </div>
      </label>
      <div className="flex gap-2 justify-end pt-1">
        <Button variant="outline" size="sm" onClick={onCancel}>Annuler</Button>
        <Button size="sm" onClick={onSave} className="bg-amber-600 hover:bg-amber-700 text-white">
          <Save className="w-3 h-3 mr-1" /> Modifier
        </Button>
      </div>
    </div>
  );
}


// ── Onglet Matériel — Inventaire outils + Inspections ────────────────────────
function OngletMateriel({ uniteId, canEdit }) {
  const API = process.env.REACT_APP_BACKEND_URL || '';
  const [outils, setOutils] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [loadingOutils, setLoadingOutils] = useState(true);
  const [view, setView] = useState('inventaire');
  const [newNom, setNewNom] = useState('');
  const [newQte, setNewQte] = useState(1);
  const [newCat, setNewCat] = useState('');
  const [adding, setAdding] = useState(false);
  const [inspection, setInspection] = useState({});

  const loadAll = async () => {
    setLoadingOutils(true);
    try {
      const [ouRes, insRes] = await Promise.all([
        axios.get(`${API}/api/unites-rangement/${uniteId}/outils`),
        axios.get(`${API}/api/unites-rangement/${uniteId}/inspections`),
      ]);
      const ouList = ouRes.data?.outils || [];
      setOutils(ouList);
      setInspections(insRes.data?.inspections || []);
      const init = {};
      ouList.forEach(o => { init[o.id] = o.dernier_statut || 'present'; });
      setInspection(init);
    } catch (e) {
      console.error('Erreur chargement outils:', e);
    }
    setLoadingOutils(false);
  };

  useEffect(() => { if (uniteId) loadAll(); }, [uniteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddOutil = async () => {
    if (!newNom.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/api/unites-rangement/${uniteId}/outils`, {
        nom: newNom.trim(), quantite: newQte, categorie: newCat,
      });
      setNewNom(''); setNewQte(1); setNewCat('');
      await loadAll();
      toast.success('Outil ajouté');
    } catch { toast.error('Erreur'); }
    setAdding(false);
  };

  const handleDeleteOutil = async (id) => {
    if (!window.confirm('Supprimer cet outil?')) return;
    await axios.delete(`${API}/api/unites-rangement/${uniteId}/outils/${id}`);
    await loadAll();
  };

  const handleSubmitInspection = async () => {
    const resultats = outils.map(o => ({
      outil_id: o.id, nom: o.nom,
      statut: inspection[o.id] || 'present',
      notes: '',
    }));
    try {
      await axios.post(`${API}/api/unites-rangement/${uniteId}/inspections`, { resultats });
      toast.success('Inspection enregistrée');
      setView('historique');
      await loadAll();
    } catch { toast.error('Erreur enregistrement inspection'); }
  };

  const STATUT_CFG = {
    present: { label: 'Présent', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    manquant: { label: 'Manquant', color: 'bg-amber-100 text-amber-800 border-amber-300' },
    brise: { label: 'Brisé', color: 'bg-red-100 text-red-800 border-red-300' },
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 border-b pb-3">
        {[
          { key: 'inventaire', label: 'Inventaire' },
          { key: 'inspection', label: "Faire l'inspection" },
          { key: 'historique', label: 'Historique' },
        ].map(v => (
          <button key={v.key} onClick={() => setView(v.key)}
            className={`text-sm px-3 py-1.5 rounded-lg font-medium transition-colors ${view === v.key ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            {v.label}
          </button>
        ))}
        {outils.filter(o => o.dernier_statut === 'brise').length > 0 && (
          <span className="ml-auto flex items-center gap-1 text-xs bg-red-100 text-red-700 border border-red-200 px-2 py-1 rounded-full">
            {outils.filter(o => o.dernier_statut === 'brise').length} outil(s) brisé(s)
          </span>
        )}
      </div>

      {loadingOutils ? (
        <div className="text-center py-8 text-slate-400">Chargement...</div>
      ) : (
        <>
          {view === 'inventaire' && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">{outils.length} outil(s) dans cette unité</p>
              {outils.length === 0 ? (
                <p className="text-slate-400 italic text-sm">Aucun outil. Ajoutez les outils de cette unité ci-dessous.</p>
              ) : (
                <div className="space-y-1.5">
                  {outils.map(o => (
                    <div key={o.id} className={`flex items-center gap-3 p-2.5 rounded-lg border ${o.dernier_statut === 'brise' ? 'bg-red-50 border-red-200' : o.dernier_statut === 'manquant' ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
                      <span className="flex-1 text-sm font-medium">{o.nom}</span>
                      {o.categorie && <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded">{o.categorie}</span>}
                      <span className="text-xs text-slate-500">x{o.quantite}</span>
                      {o.dernier_statut && o.dernier_statut !== 'present' && (
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUT_CFG[o.dernier_statut]?.color}`}>
                          {STATUT_CFG[o.dernier_statut]?.label}
                        </span>
                      )}
                      {canEdit && (
                        <button onClick={() => handleDeleteOutil(o.id)} className="text-slate-300 hover:text-red-500">X</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {canEdit && (
                <div className="border-t pt-3 space-y-2">
                  <p className="text-xs font-semibold text-slate-600">Ajouter un outil</p>
                  <div className="flex gap-2 flex-wrap">
                    <Input value={newNom} onChange={e => setNewNom(e.target.value)} placeholder="Nom de l'outil" className="flex-1 min-w-32 text-sm" onKeyDown={e => e.key === 'Enter' && handleAddOutil()} data-testid="input-outil-nom" />
                    <Input type="number" value={newQte} onChange={e => setNewQte(parseInt(e.target.value)||1)} min={1} max={99} className="w-16 text-sm" />
                    <Input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="Catégorie" className="w-32 text-sm" />
                    <Button size="sm" onClick={handleAddOutil} disabled={adding || !newNom.trim()} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                      {adding ? '...' : '+ Ajouter'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {view === 'inspection' && (
            <div className="space-y-3">
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm text-indigo-800">
                <p className="font-semibold">Inspection hebdomadaire</p>
                <p className="text-xs mt-0.5">Cochez le statut de chaque outil. Cette inspection sera enregistrée dans l'historique.</p>
              </div>
              {outils.length === 0 ? (
                <p className="text-slate-400 italic text-sm">Ajoutez d'abord des outils dans l'inventaire.</p>
              ) : (
                <>
                  <div className="space-y-2">
                    {outils.map(o => (
                      <div key={o.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-white border border-slate-200">
                        <span className="flex-1 text-sm font-medium">{o.nom} <span className="text-slate-400 font-normal">x{o.quantite}</span></span>
                        <div className="flex gap-1">
                          {Object.entries(STATUT_CFG).map(([key, cfg]) => (
                            <button key={key} type="button"
                              onClick={() => setInspection(prev => ({ ...prev, [o.id]: key }))}
                              className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-all ${inspection[o.id] === key ? cfg.color + ' scale-105' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400'}`}
                              data-testid={`inspection-${o.id}-${key}`}>
                              {cfg.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button onClick={handleSubmitInspection} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="btn-submit-inspection">
                    Enregistrer l'inspection
                  </Button>
                </>
              )}
            </div>
          )}

          {view === 'historique' && (
            <div className="space-y-3">
              {inspections.length === 0 ? (
                <p className="text-slate-400 italic text-sm">Aucune inspection enregistrée.</p>
              ) : (
                <>
                  {outils.filter(o => o.dernier_statut === 'brise').length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
                      <p className="text-sm font-semibold text-red-800">Outils à remplacer</p>
                      {outils.filter(o => o.dernier_statut === 'brise').map(o => (
                        <div key={o.id} className="flex items-center justify-between text-sm text-red-700">
                          <span>{o.nom}</span>
                          <span className="text-xs text-red-500">{o.date_brise?.substring(0,10)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="space-y-2">
                    {inspections.slice(0, 10).map(ins => (
                      <div key={ins.id} className={`p-3 rounded-lg border text-sm ${ins.statut_global === 'ok' ? 'bg-emerald-50 border-emerald-200' : ins.statut_global === 'critique' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold">{ins.date_inspection?.substring(0,10)} — Semaine {ins.semaine}</span>
                          <span className="text-xs">{ins.chef_nom}</span>
                        </div>
                        <div className="flex gap-3 text-xs">
                          <span className="text-emerald-700">{ins.nb_outils_ok} présents</span>
                          {ins.nb_brise > 0 && <span className="text-red-700">{ins.nb_brise} brisé(s)</span>}
                          {ins.nb_manquant > 0 && <span className="text-amber-700">{ins.nb_manquant} manquant(s)</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ── Onglet Entretiens ─────────────────────────────────────────────────────────
function OngletEntretiens({ vehiculeId, vehiculeNom, canEdit, canDeclarer = true }) {
  const [entretiens, setEntretiens] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showDeclarerForm, setShowDeclarerForm] = useState(false);
  const [form, setForm] = useState({ date: "", type_entretien: "", description: "", kilometrage: "", kilometrage_actuel: "", cout: "", fournisseur: "", notes: "" });
  const [declarForm, setDeclarForm] = useState({ type_entretien: "", description: "", urgence: "normal" });

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/api/vehicules/${vehiculeId}/entretiens`);
    if (r.status === 200) { const d = r.data; setEntretiens(d.entretiens || []); }
  }, [vehiculeId]);

  useEffect(() => { load(); }, [load]);

  // 🛢️ [15] Helper : imprime l'étiquette de prochain changement d'huile pour un entretien.
  const imprimerEtiquetteHuile = (entretien) => {
    const prochainKm = entretien.prochain_changement_km
      || ((entretien.kilometrage_actuel || entretien.kilometrage || 0) + 8000);
    if (!prochainKm || prochainKm <= 0) {
      toast.error("Kilométrage manquant — impossible d'imprimer");
      return;
    }
    try {
      dymoPrintHuileLabel({
        uniteNom: vehiculeNom || '—',
        prochainKm,
        dateChangement: entretien.date,
      });
      toast.success("Étiquette envoyée à l'impression");
    } catch (e) {
      toast.error(e?.message || "Erreur d'impression");
    }
  };

  const isHuileType = TYPES_HUILE.has(form.type_entretien);

  const save = async () => {
    // 🛢️ [15] Validation côté frontend : kilométrage requis pour changement d'huile
    if (isHuileType) {
      const km = parseInt(form.kilometrage_actuel, 10);
      if (!km || km <= 0 || isNaN(km)) {
        toast.error("Le kilométrage actuel est requis pour un changement d'huile");
        return;
      }
    }
    const payload = {
      ...form,
      kilometrage: parseInt(form.kilometrage) || (isHuileType ? parseInt(form.kilometrage_actuel) : null),
      kilometrage_actuel: isHuileType ? parseInt(form.kilometrage_actuel) : null,
      cout: parseFloat(form.cout) || null,
    };
    const r = await axios.post(`${API}/api/vehicules/${vehiculeId}/entretiens`, payload);
    if (r.status === 200) {
      toast.success("Entretien ajouté");
      const created = r.data;
      setShowForm(false);
      setForm({ date: "", type_entretien: "", description: "", kilometrage: "", kilometrage_actuel: "", cout: "", fournisseur: "", notes: "" });
      await load();
      // 🛢️ [15] Si changement d'huile : proposer d'imprimer l'étiquette tout de suite
      if (created && TYPES_HUILE.has(created.type_entretien)) {
        setTimeout(() => {
          if (window.confirm(`Imprimer l'étiquette de prochain changement d'huile ?\nProchain à : ${(created.prochain_changement_km || 0).toLocaleString('fr-CA')} km`)) {
            imprimerEtiquetteHuile(created);
          }
        }, 300);
      }
    }
    else toast.error("Erreur");
  };

  // ✅ 2026-04-27 : Déclaration d'entretien à faire (par le chef d'équipe)
  const declarer = async () => {
    if (!declarForm.type_entretien && !declarForm.description.trim()) {
      toast.error("Précisez le type ou la description");
      return;
    }
    const payload = {
      date: new Date().toISOString().slice(0, 10),
      type_entretien: declarForm.type_entretien || "Demande chef d'équipe",
      description: `🛠️ DEMANDE — ${declarForm.urgence.toUpperCase()} : ${declarForm.description}`,
      kilometrage: null,
      cout: null,
      fournisseur: "",
      notes: "À planifier",
      statut: "demande", // ✅ pour différencier des entretiens réalisés
      urgence: declarForm.urgence,
    };
    const r = await axios.post(`${API}/api/vehicules/${vehiculeId}/entretiens`, payload);
    if (r.status === 200) {
      toast.success("Entretien déclaré — l'administration sera avisée");
      setShowDeclarerForm(false);
      setDeclarForm({ type_entretien: "", description: "", urgence: "normal" });
      load();
    } else {
      toast.error("Erreur");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cet entretien ?")) return;
    const r = await axios.delete(`${API}/api/vehicules/${vehiculeId}/entretiens/${id}`);
    if (r.status === 200) { toast.success("Supprimé"); load(); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {canEdit && (
          <Button size="sm" onClick={() => { setShowDeclarerForm(false); setShowForm(true); }} className="bg-[#1a237e] hover:bg-[#1a237e]/90 text-white">
            <Plus className="w-4 h-4 mr-1" /> Ajouter un entretien
          </Button>
        )}
        {/* ✅ 2026-04-27 : Bouton "+ Déclarer un entretien" accessible aux chefs d'équipe */}
        {canDeclarer && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setShowForm(false); setShowDeclarerForm(true); }}
            className="border-amber-400 text-amber-700 hover:bg-amber-50"
            data-testid="btn-declarer-entretien"
          >
            <Plus className="w-4 h-4 mr-1" /> Déclarer un entretien
          </Button>
        )}
      </div>

      {/* Formulaire DÉCLARATION (chef d'équipe / tout le monde) */}
      {showDeclarerForm && (
        <div className="border-2 border-amber-300 rounded-lg p-4 bg-amber-50 space-y-3" data-testid="form-declarer-entretien">
          <h4 className="font-semibold text-amber-900 flex items-center gap-2">
            🛠️ Déclarer un entretien à faire
          </h4>
          <p className="text-xs text-amber-700">Signalez un problème (changement d'huile, check engine, bruit suspect, etc.). L'administration sera avisée et planifiera l'entretien.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-amber-900 font-semibold">Type</label>
              <Select value={declarForm.type_entretien} onValueChange={v => setDeclarForm(f => ({ ...f, type_entretien: v }))}>
                <SelectTrigger className="mt-1 h-8"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>{TYPES_ENTRETIEN.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-amber-900 font-semibold">Urgence</label>
              <Select value={declarForm.urgence} onValueChange={v => setDeclarForm(f => ({ ...f, urgence: v }))}>
                <SelectTrigger className="mt-1 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">🟢 Normal — prochaine occasion</SelectItem>
                  <SelectItem value="elevee">🟠 Élevée — cette semaine</SelectItem>
                  <SelectItem value="urgent">🔴 Urgent — bloque le travail</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs text-amber-900 font-semibold">Description du problème *</label>
            <Textarea
              value={declarForm.description}
              onChange={e => setDeclarForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Ex: Bruit de cliquetis quand j'accélère. Voyant moteur allumé depuis ce matin."
              rows={3}
              className="mt-1 text-sm"
              data-testid="declarer-description"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setShowDeclarerForm(false)}>Annuler</Button>
            <Button size="sm" onClick={declarer} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="btn-save-declaration">
              Déclarer
            </Button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 space-y-3">
          <h4 className="font-semibold text-slate-800">Nouvel entretien</h4>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-500">Date *</label>
              <Input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} className="mt-1 h-8" /></div>
            <div><label className="text-xs text-slate-500">Type</label>
              <Select value={form.type_entretien} onValueChange={v => setForm(f => ({...f, type_entretien: v}))}>
                <SelectTrigger className="mt-1 h-8"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>{TYPES_ENTRETIEN.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs text-slate-500">Kilométrage</label>
              <Input type="number" value={form.kilometrage} onChange={e => setForm(f => ({...f, kilometrage: e.target.value}))} className="mt-1 h-8" data-testid="input-km" /></div>
            {isHuileType && (
              <div className="col-span-2 bg-amber-50 border border-amber-300 rounded p-2 space-y-2" data-testid="bloc-huile">
                <div>
                  <label className="text-xs text-amber-900 font-semibold">Kilométrage actuel * (changement d'huile)</label>
                  <Input
                    type="number"
                    min="1"
                    value={form.kilometrage_actuel}
                    onChange={e => setForm(f => ({...f, kilometrage_actuel: e.target.value}))}
                    placeholder="ex: 112000"
                    className="mt-1 h-8"
                    data-testid="input-km-actuel"
                  />
                </div>
                {(() => {
                  const km = parseInt(form.kilometrage_actuel, 10);
                  if (km && km > 0) {
                    return (
                      <p className="text-sm font-bold text-amber-900" data-testid="preview-prochain-km">
                        🛢️ Prochain changement à : {(km + 8000).toLocaleString('fr-CA')} km
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
            <div><label className="text-xs text-slate-500">Coût ($)</label>
              <Input type="number" step="0.01" value={form.cout} onChange={e => setForm(f => ({...f, cout: e.target.value}))} className="mt-1 h-8" /></div>
            <div className="col-span-2"><label className="text-xs text-slate-500">Description</label>
              <Input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Détails..." className="mt-1 h-8" /></div>
            <div><label className="text-xs text-slate-500">Fournisseur</label>
              <Input value={form.fournisseur} onChange={e => setForm(f => ({...f, fournisseur: e.target.value}))} placeholder="Jiffy Lube" className="mt-1 h-8" /></div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Annuler</Button>
            <Button size="sm" onClick={save} className="bg-[#1a237e] text-white">Enregistrer</Button>
          </div>
        </div>
      )}
      {entretiens.length === 0 ? (
        <div className="text-center py-10 text-slate-400">Aucun entretien enregistré</div>
      ) : (
        <div className="space-y-2">
          {entretiens.map(e => {
            const isDemande = e.statut === 'demande' || (e.description || '').startsWith('🛠️ DEMANDE');
            const urgence = e.urgence || 'normal';
            const couleurUrg = isDemande
              ? (urgence === 'urgent' ? 'border-red-300 bg-red-50' : urgence === 'elevee' ? 'border-amber-300 bg-amber-50' : 'border-blue-200 bg-blue-50')
              : 'border-slate-200 hover:bg-slate-50';
            return (
              <div key={e.id} className={`flex items-start justify-between border rounded-lg p-3 ${couleurUrg}`}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#1a237e]/10 flex items-center justify-center shrink-0 mt-0.5">
                    {isDemande ? <span className="text-base">🛠️</span> : <Wrench className="w-4 h-4 text-[#1a237e]" />}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">
                      {e.type_entretien}
                      {isDemande && (
                        <span className={`ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${urgence === 'urgent' ? 'bg-red-600 text-white' : urgence === 'elevee' ? 'bg-amber-500 text-white' : 'bg-blue-500 text-white'}`}>
                          DEMANDE · {urgence}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500">{e.date} · {e.kilometrage ? `${e.kilometrage.toLocaleString()} km` : ""} {e.fournisseur ? `· ${e.fournisseur}` : ""}</p>
                    {e.description && <p className="text-sm text-slate-700 mt-0.5">{e.description}</p>}
                    {/* 🛢️ [15] Rappel "Prochain changement" affiché en italique pour les huiles */}
                    {TYPES_HUILE.has(e.type_entretien) && e.prochain_changement_km && (
                      <p className="text-xs text-amber-700 mt-0.5 italic" data-testid={`prochain-km-${e.id}`}>
                        🛢️ Prochain à : {e.prochain_changement_km.toLocaleString('fr-CA')} km
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {e.cout && <span className="text-sm font-medium text-slate-700">{e.cout.toFixed(2)} $</span>}
                  {/* 🛢️ [15] Bouton "Réimprimer étiquette" pour les changements d'huile */}
                  {TYPES_HUILE.has(e.type_entretien) && !isDemande && (
                    <button
                      onClick={() => imprimerEtiquetteHuile(e)}
                      className="text-slate-400 hover:text-blue-600 transition-colors"
                      title="Réimprimer l'étiquette de prochain changement"
                      data-testid={`btn-reimprimer-${e.id}`}
                    >
                      <Printer className="w-4 h-4" />
                    </button>
                  )}
                  {canEdit && (
                    <button onClick={() => del(e.id)} className="text-slate-400 hover:text-red-500 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Onglet Incidents ──────────────────────────────────────────────────────────
function OngletIncidents({ vehiculeId, vehiculeNom, user, canCreate, canEdit }) {
  const [incidents, setIncidents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ description: "", notes: "" });
  const [uploading, setUploading] = useState(false);
  const [photos, setPhotos] = useState([]);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/api/vehicules/${vehiculeId}/incidents`);
    if (r.status === 200) { const d = r.data; setIncidents(d.incidents || []); }
  }, [vehiculeId]);

  useEffect(() => { load(); }, [load]);

  const uploadPhoto = async (file) => {
    setUploading(true);
    try {
      const { compressImageIfNeeded } = await import('@/lib/imageCompression');
      const compressed = await compressImageIfNeeded(file);
      const formData = new FormData();
      formData.append("file", compressed);
      const r = await axios.post(`${API}/api/crm/upload-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (r.status === 200) { const d = r.data; setPhotos(ps => [...ps, d.photo_url]); }
    } catch { toast.error("Erreur upload photo"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.description.trim()) { toast.error("Description requise"); return; }
    const r = await axios.post(`${API}/api/vehicules/${vehiculeId}/incidents`, {
      ...form,
      photos,
      declare_par_id: user?.id,
      declare_par_nom: `${user?.prenom || ""} ${user?.nom || ""}`.trim()
    });
    if (r.status === 200) { toast.success("Rapport soumis"); setShowForm(false); setForm({ description: "", notes: "" }); setPhotos([]); load(); }
    else toast.error("Erreur lors de la soumission");
  };

  const updateStatut = async (incidentId, statut) => {
    const r = await axios.put(`${API}/api/vehicules/${vehiculeId}/incidents/${incidentId}`, { statut });
    if (r.status === 200) { toast.success("Statut mis à jour"); load(); }
  };

  const STATUTS = {
    "nouveau": { label: "Nouveau", color: "bg-red-100 text-red-700", icon: AlertTriangle },
    "en_traitement": { label: "En traitement", color: "bg-amber-100 text-amber-700", icon: Clock },
    "resolu": { label: "Résolu", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  };

  return (
    <div className="space-y-4">
      {canCreate && (
        <Button size="sm" onClick={() => setShowForm(true)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="new-incident-btn">
          <AlertTriangle className="w-4 h-4 mr-1" /> Déclarer un bris
        </Button>
      )}
      {showForm && (
        <div className="border border-red-200 rounded-lg p-4 bg-red-50 space-y-3">
          <h4 className="font-semibold text-red-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Rapport de bris — {vehiculeNom}
          </h4>
          <div>
            <label className="text-xs text-red-700 font-medium">Description du bris *</label>
            <Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={3} placeholder="Décrivez le bris en détail..." className="mt-1 border-red-200" />
          </div>
          <div>
            <label className="text-xs text-red-700 font-medium">Photos</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {photos.map((url, i) => (
                <div key={url.id || url._id || i} className="relative">
                  <img src={url} alt="" className="w-20 h-20 object-cover rounded border" />
                  <button onClick={() => setPhotos(ps => ps.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <label className={`w-20 h-20 border-2 border-dashed border-red-300 rounded flex flex-col items-center justify-center cursor-pointer hover:bg-red-100 transition-colors ${uploading ? "opacity-50" : ""}`}>
                <Camera className="w-5 h-5 text-red-400" />
                <span className="text-xs text-red-400 mt-1">{uploading ? "..." : "Ajouter"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && uploadPhoto(e.target.files[0])} />
              </label>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => { setShowForm(false); setPhotos([]); }}>Annuler</Button>
            <Button size="sm" onClick={submit} className="bg-red-600 hover:bg-red-700 text-white">Soumettre le rapport</Button>
          </div>
        </div>
      )}
      {incidents.length === 0 ? (
        <div className="text-center py-10 text-slate-400">Aucun incident déclaré</div>
      ) : (
        <div className="space-y-3">
          {incidents.map(inc => {
            const s = STATUTS[inc.statut] || STATUTS["nouveau"];
            const SIcon = s.icon;
            return (
              <div key={inc.id} className={`border rounded-lg p-4 ${inc.statut === "nouveau" ? "border-red-200 bg-red-50" : "border-slate-200"}`} data-testid={`incident-${inc.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>
                        <SIcon className="w-3 h-3" /> {s.label}
                      </span>
                      <span className="text-xs text-slate-500">{inc.created_at?.split("T")[0]}</span>
                    </div>
                    <p className="text-sm text-slate-800">{inc.description}</p>
                    <p className="text-xs text-slate-500 mt-1">Déclaré par : {inc.declare_par_nom}</p>
                    {inc.photos?.length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {inc.photos.map((url, i) => (
                          <a key={url.id || url._id || i} href={url} target="_blank" rel="noreferrer">
                            <img src={url} alt="" className="w-16 h-16 object-cover rounded border hover:opacity-80" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  {canEdit && inc.statut !== "resolu" && (
                    <div className="flex flex-col gap-1">
                      {inc.statut === "nouveau" && (
                        <Button size="sm" variant="outline" className="text-amber-700 border-amber-300 h-7 text-xs" onClick={() => updateStatut(inc.id, "en_traitement")}>
                          En traitement
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300 h-7 text-xs" onClick={() => updateStatut(inc.id, "resolu")}>
                        <CheckCircle className="w-3 h-3 mr-1" /> Résolu
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ── Page principale ───────────────────────────────────────────────────────────
export function Vehicules() {
  const { user } = useAuth();
  const [vehicules, setVehicules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [mainTab, setMainTab] = useState("vehicules");

  // Unités de rangement state
  const [unites, setUnites] = useState([]);
  const [uniteLoading, setUniteLoading] = useState(false);
  const [showCreateRangement, setShowCreateRangement] = useState(false);
  const [newUniteNumero, setNewUniteNumero] = useState("");
  const [newUniteDesc, setNewUniteDesc] = useState("");
  const [newUniteCode, setNewUniteCode] = useState(""); // 🆕 # d'unité
  const [newUniteType, setNewUniteType] = useState("exterieur");
  const [newUnitePlaque, setNewUnitePlaque] = useState("");
  const [newUniteSerie, setNewUniteSerie] = useState("");
  const [newUniteAssurance, setNewUniteAssurance] = useState("");
  const [newUniteRemorque, setNewUniteRemorque] = useState(false);
  const [newUniteTravail, setNewUniteTravail] = useState(false);
  // 🆕 [Phase 86 Sprint Parking 1 — 2026-06-23] Type physique explicite
  // (vehicule | remorque | equipement_chantier). Source de vérité pour la catégorie Parking.
  const [newUniteTypePhysique, setNewUniteTypePhysique] = useState("remorque");
  const [savingUnite, setSavingUnite] = useState(false);
  const [showCreateNacelle, setShowCreateNacelle] = useState(false);
  const [showInspectionHub, setShowInspectionHub] = useState(false);
  // 🆕 [Phase 35] Dialog centralisé Normes de matériel
  const [showNormesDialog, setShowNormesDialog] = useState(false);

  const { canDo, user: permsUser } = usePermissions();
  // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Branchement RBAC via `unites_editer`.
  // Contrôle : bouton "Modifier"/"Créer"/"Supprimer" unité + resume hebdo.
  // Backend Vague 1/2c gère déjà l'écriture sur `/api/vehicules/*` via require_owner.
  const canEdit = !!(canDo && canDo('unites_editer'));
  // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Branchement RBAC via `unites_declarer_incident`.
  // 🔒 Cat H additionnel : rôle_terrain / role_admin sont des identités (pas RBAC) — préservé
  // pour couvrir les cas où le JWT n'expose que role_admin/role_terrain sans role principal.
  const canDeclareIncident = (canDo && canDo('unites_declarer_incident'))
    || canEdit
    || user?.role_terrain === "chef_equipe"
    || user?.role_terrain === "installateur" || user?.role_admin === "installateur"
    || user?.role_admin === "charge_projet";
  const unitesVoirPropre = canDo('unites_voir_propre_seulement') && !canEdit;

  // Cat H — identités terrain (chef d'équipe / installateur / sous-traitant) : dispatch UI en lecture seule des unités assignées, pas gate RBAC. ✅ 2026-04-27
  // Tous voient uniquement les unités qui leur sont assignées (lecture seule)
  const isChefEquipe = (aLeRole(user, 'chef_equipe')
                        || aLeRole(user, 'installateur')
                        || (user?.statut === "sous_traitant" || user?.type_employe === "sous_traitant"))
                       && !canEdit;
  // ID des unités assignées à cet employé (multi)
  const myUniteIds = (permsUser?.unites_assignees || user?.unites_assignees || []).map(u => u.unite_id || u.id).filter(Boolean);
  const canVoirResumeHebdo = canEdit; // pas pour chef d'équipe
  const canCreerUnite = canEdit; // pas pour chef d'équipe
  const canSupprimerUnite = canEdit;
  const canModifierUnite = canEdit;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/vehicules`);
      if (r.status === 200) { setVehicules(r.data.vehicules || []); }
    } finally { setLoading(false); }
  }, []);

  const loadUnites = useCallback(async () => {
    setUniteLoading(true);
    try {
      const r = await axios.get(`${API}/api/unites-rangement`);
      if (r.status === 200) { setUnites(r.data.unites || []); }
    } finally { setUniteLoading(false); }
  }, []);

  useEffect(() => { load(); loadUnites(); }, [load, loadUnites]);

  const filteredVehicules = (() => {
    // ✅ 2026-04-27 : Chef d'équipe ne voit que ses unités (multi-assignation)
    if (isChefEquipe) {
      if (myUniteIds.length === 0) {
        // Fallback ancien système
        return vehicules.filter(v => v.assigne_a?.id === user?.employee_id);
      }
      return vehicules.filter(v => myUniteIds.includes(v.id) || v.assigne_a?.id === user?.employee_id);
    }
    if (unitesVoirPropre) {
      return vehicules.filter(v => v.assigne_a?.id === user?.employee_id);
    }
    return vehicules;
  })();

  const createVehicule = async (form) => {
    const r = await axios.post(`${API}/api/vehicules`, form);
    if (r.status === 200) { toast.success("Véhicule ajouté"); setShowCreate(false); load(); }
    else toast.error("Erreur lors de la création");
  };

  const updateVehicule = async (form) => {
    const r = await axios.put(`${API}/api/vehicules/${selected.id}`, form);
    if (r.status === 200) { toast.success("Véhicule mis à jour"); setShowEdit(false); load(); setSelected(r.data); }
    else toast.error("Erreur lors de la mise à jour");
  };

  const deleteVehicule = async (id) => {
    if (!window.confirm("Supprimer cette unité et tout son historique ?")) return;
    const r = await axios.delete(`${API}/api/vehicules/${id}`);
    if (r.status === 200) { toast.success("Véhicule supprimé"); setSelected(null); load(); }
  };

  const sendResume = async () => {
    setResumeLoading(true);
    try {
      const r = await axios.post(`${API}/api/vehicules/resume-hebdomadaire/envoyer`);
      if (r.status === 200) toast.success(`Résumé envoyé à ${r.data.envois} chargée(s) de projet`);
      else toast.error("Erreur lors de l'envoi");
    } finally { setResumeLoading(false); }
  };

  const createUnite = async () => {
    if (!newUniteNumero.trim()) { toast.error("Le numéro de l'unité est requis"); return; }
    setSavingUnite(true);
    try {
      const r = await axios.post(`${API}/api/unites-rangement`, {
        numero: newUniteNumero.trim(), 
        description: newUniteDesc.trim(), 
        numero_unite: newUniteCode.trim(), // 🆕 Ajout du # d'unité
        type_utilisation: newUniteType,
        plaque: newUnitePlaque.trim(), 
        numero_serie: newUniteSerie.trim(),
        assurance_expiration: newUniteAssurance || null,
        est_remorque_echafauds: newUniteRemorque, 
        est_unite_travail: newUniteTravail,
        // 🆕 [Phase 86 Sprint Parking 1] Type physique = NOUVELLE source de vérité
        // pour la catégorisation Parking (vehicule | remorque | equipement_chantier).
        type_physique: newUniteTypePhysique,
      });
      if (r.status === 200) {
        toast.success(`Unité "${newUniteNumero}" créée`);
        setNewUniteNumero(""); setNewUniteDesc(""); setNewUniteCode(""); setNewUniteType("exterieur");
        setNewUnitePlaque(""); setNewUniteSerie(""); setNewUniteAssurance("");
        setNewUniteRemorque(false); setNewUniteTravail(false);
        setNewUniteTypePhysique("remorque");
        setShowCreateRangement(false);
        loadUnites();
      } else { toast.error(r.data?.detail || "Erreur création"); }
    } finally { setSavingUnite(false); }
  };

  const deleteUnite = async (id, numero) => {
    if (!window.confirm(`Supprimer l'unité de rangement "${numero}" ?`)) return;
    const r = await axios.delete(`${API}/api/unites-rangement/${id}`);
    if (r.status === 200) { toast.success("Unité supprimée"); loadUnites(); }
  };

  const [selectedUniteId, setSelectedUniteId] = useState(null);
  const [editUnite, setEditUnite] = useState(null);
  const saveEditUnite = async () => {
    if (!editUnite?.id) return;
    try {
      // ✅ FIX: Filtrer les pièces vides avant la sauvegarde
      const typesEtagesFiltered = (editUnite.types_etages || []).map(type => ({
        ...type,
        composition: (type.composition || []).filter(comp => comp.piece && comp.piece !== '__empty__' && comp.quantite_par_etage > 0)
      }));

      await axios.put(`${API}/api/unites-rangement/${editUnite.id}`, {
        numero: editUnite.numero, 
        numero_unite: editUnite.numero_unite || '',
        description: editUnite.description || '',
        type_utilisation: editUnite.type_utilisation || 'exterieur',
        est_remorque_echafauds: editUnite.est_remorque_echafauds || false,
        est_unite_travail: editUnite.est_unite_travail || false,
        // Nouveaux inventaires échafaudages (avec filtrage)
        types_etages: typesEtagesFiltered,
        inventaire_pieces: editUnite.inventaire_pieces || [],
        // Ancien format (rétrocompatibilité)
        inventaire_echafauds: editUnite.inventaire_echafauds || [],
        plaque: editUnite.plaque || '', 
        numero_serie: editUnite.numero_serie || '',
        assurance_expiration: editUnite.assurance_expiration || null,
      });
      toast.success('Unité mise à jour');
      // Nettoyer la sauvegarde automatique
      localStorage.removeItem(`editUnite_${editUnite.id}_backup`);
      setEditUnite(null);
      loadUnites();
    } catch { toast.error('Erreur sauvegarde'); }
  };

  // 🔥 SAUVEGARDE AUTOMATIQUE pour éviter la perte de données
  useEffect(() => {
    if (editUnite?.id && (editUnite.inventaire_pieces?.length > 0 || editUnite.types_etages?.length > 0)) {
      const backupKey = `editUnite_${editUnite.id}_backup`;
      localStorage.setItem(backupKey, JSON.stringify({
        inventaire_pieces: editUnite.inventaire_pieces,
        types_etages: editUnite.types_etages,
        timestamp: new Date().toISOString()
      }));
    }
  }, [editUnite?.inventaire_pieces, editUnite?.types_etages, editUnite?.id]);

  // Restaurer depuis la sauvegarde au chargement
  useEffect(() => {
    if (editUnite?.id && (!editUnite.inventaire_pieces || editUnite.inventaire_pieces.length === 0)) {
      const backupKey = `editUnite_${editUnite.id}_backup`;
      const backup = localStorage.getItem(backupKey);
      if (backup) {
        try {
          const data = JSON.parse(backup);
          if (data.inventaire_pieces || data.types_etages) {
            const shouldRestore = window.confirm(
              `🔄 Sauvegarde automatique trouvée (${formatLocalTime(data.timestamp)}). Voulez-vous restaurer vos données ?`
            );
            if (shouldRestore) {
              setEditUnite(prev => ({
                ...prev,
                inventaire_pieces: data.inventaire_pieces || [],
                types_etages: data.types_etages || []
              }));
              toast.success('✅ Données restaurées depuis la sauvegarde automatique');
            }
          }
        } catch (e) {
          console.error('Erreur restauration:', e);
        }
      }
    }
  }, [editUnite?.id]);

  const expirationStatus = (dateStr) => {
    if (!dateStr) return null;
    const exp = new Date(dateStr);
    const today = new Date();
    const daysLeft = Math.floor((exp - today) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return { color: "text-red-600", label: `Expiré (${Math.abs(daysLeft)}j)` };
    if (daysLeft < 30) return { color: "text-amber-600", label: `${daysLeft}j restants` };
    return { color: "text-emerald-600", label: `${daysLeft}j` };
  };

  // KPI stats
  const vehKpi = {
    total: filteredVehicules.length,
    incidents: filteredVehicules.reduce((s, v) => s + (v.incidents_ouverts || 0), 0),
    assExp: filteredVehicules.filter(v => v.assurance_expiration && new Date(v.assurance_expiration) < new Date()).length,
    inspExp: filteredVehicules.filter(v => v.inspection_expiration && new Date(v.inspection_expiration) < new Date()).length,
  };
  const rngKpi = {
    total: unites.length,
    travail: unites.filter(u => u.est_unite_travail).length,
    echafauds: unites.filter(u => u.est_remorque_echafauds).length,
  };

  const handleAddUnit = () => {
    if (mainTab === 'vehicules') setShowCreate(true);
    else if (mainTab === 'rangement') setShowCreateRangement(true);
    else if (mainTab === 'elevation') setShowCreateNacelle(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between relative z-30">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#1a237e] flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Unités</h1>
            <p className="text-slate-500 text-sm">Gestion des unités de travail, rangement et élévation</p>
          </div>
        </div>
        <div className="flex gap-2 relative z-30">
          {/* ✅ Bouton Hub Inspection Rangement (visible pour tous, lecture filtrée pour chefs) */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowInspectionHub(true)}
            data-testid="btn-inspection-hub"
          >
            <ClipboardCheck className="w-4 h-4 mr-1" />
            Inspections de rangement
          </Button>
          {/* 🆕 [Phase 35] Bouton centralisé Normes de matériel */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowNormesDialog(true)}
            data-testid="btn-normes-materiel-top"
          >
            📋 Normes de matériel
          </Button>
          {canEdit && mainTab === "vehicules" && (
            <Button variant="outline" size="sm" onClick={sendResume} disabled={resumeLoading} data-testid="send-resume-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${resumeLoading ? "animate-spin" : ""}`} />
              Résumé hebdo
            </Button>
          )}
          {canEdit && mainTab === "vehicules" && (
            <Button
              variant="outline" size="sm"
              onClick={() => window.location.assign('/unites/comparaison-materiel')}
              data-testid="btn-comparaison-materiel"
            >
              <Package className="w-4 h-4 mr-1" /> Comparer matériel
            </Button>
          )}
          {canEdit && (
            <Button size="sm" onClick={handleAddUnit} className="bg-[#1a237e] hover:bg-[#1a237e]/90 text-white" data-testid="add-vehicule-btn">
              <Plus className="w-4 h-4 mr-1" /> Ajouter unité
            </Button>
          )}
        </div>
      </div>

      {/* ✅ Hub central pour Historique global + Critères + Paramètres */}
      <InspectionRangementHubDialog
        open={showInspectionHub}
        onOpenChange={setShowInspectionHub}        canEdit={canEdit}
      />
      {/* 🆕 [Phase 35] NormesMaterielDialog centralisé top-level */}
      <NormesMaterielDialog
        open={showNormesDialog}
        onClose={() => setShowNormesDialog(false)}
        unitesFromParent={vehicules}
      />

      {/* Tabs */}
      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList>
          <TabsTrigger value="vehicules" data-testid="tab-vehicules">
            <Car className="w-4 h-4 mr-1.5" /> Véhicules <span className="ml-1 text-xs text-slate-400">({filteredVehicules.length})</span>
          </TabsTrigger>
          {canEdit && (
            <TabsTrigger value="rangement" data-testid="tab-rangement">
              <Package className="w-4 h-4 mr-1.5" /> Remorques <span className="ml-1 text-xs text-slate-400">({unites.length})</span>
            </TabsTrigger>
          )}
          {!isChefEquipe && (
            <TabsTrigger value="elevation" data-testid="tab-elevation">
              <Wrench className="w-4 h-4 mr-1.5" /> Équipement Chantier
            </TabsTrigger>
          )}
        </TabsList>

        {/* ─── Onglet Unités de travail — STACKED CARDS ─── */}
        <TabsContent value="vehicules">
          <div className="space-y-3 mt-4">
            {!loading && filteredVehicules.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="kpi-travail">
                <div className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-lg px-3 py-2.5">
                  <div className="w-8 h-8 rounded-lg bg-[#1a237e]/10 flex items-center justify-center shrink-0"><Car className="w-4 h-4 text-[#1a237e]" /></div>
                  <div><p className="text-lg font-bold text-slate-900 leading-none">{vehKpi.total}</p><p className="text-[10px] text-slate-500 uppercase font-medium mt-0.5">Unités</p></div>
                </div>
                <div className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-lg px-3 py-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${vehKpi.incidents > 0 ? 'bg-red-100' : 'bg-emerald-100'}`}>
                    <AlertTriangle className={`w-4 h-4 ${vehKpi.incidents > 0 ? 'text-red-600' : 'text-emerald-600'}`} />
                  </div>
                  <div><p className={`text-lg font-bold leading-none ${vehKpi.incidents > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{vehKpi.incidents}</p><p className="text-[10px] text-slate-500 uppercase font-medium mt-0.5">Incidents</p></div>
                </div>
                <div className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-lg px-3 py-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${vehKpi.assExp > 0 ? 'bg-red-100' : 'bg-emerald-100'}`}>
                    <CheckCircle className={`w-4 h-4 ${vehKpi.assExp > 0 ? 'text-red-600' : 'text-emerald-600'}`} />
                  </div>
                  <div><p className={`text-lg font-bold leading-none ${vehKpi.assExp > 0 ? 'text-red-600' : 'text-slate-900'}`}>{vehKpi.assExp}</p><p className="text-[10px] text-slate-500 uppercase font-medium mt-0.5">Ass. expirées</p></div>
                </div>
                <div className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-lg px-3 py-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${vehKpi.inspExp > 0 ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                    <Clock className={`w-4 h-4 ${vehKpi.inspExp > 0 ? 'text-amber-600' : 'text-emerald-600'}`} />
                  </div>
                  <div><p className={`text-lg font-bold leading-none ${vehKpi.inspExp > 0 ? 'text-amber-600' : 'text-slate-900'}`}>{vehKpi.inspExp}</p><p className="text-[10px] text-slate-500 uppercase font-medium mt-0.5">Insp. expirées</p></div>
                </div>
              </div>
            )}
            {loading ? (
              <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a237e]" /></div>
            ) : filteredVehicules.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-xl" data-testid="vehicules-empty">
                <Car className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-sm text-slate-500 font-medium">{unitesVoirPropre ? 'Aucune unité assignée à votre compte' : 'Aucune unité enregistrée'}</p>
                <p className="text-xs text-slate-400 mt-1">Cliquez sur "Ajouter unité" pour commencer</p>
              </div>
            ) : filteredVehicules.map(v => {
              const isExpanded = selected?.id === v.id;
              const assStatus = expirationStatus(v.assurance_expiration);
              const inspStatus = expirationStatus(v.inspection_expiration);
              return (
                <div key={v.id} className={`${CARD_BASE} ${isExpanded ? 'border-[#1a237e] shadow-md ring-1 ring-[#1a237e]/10' : CARD_IDLE}`} data-testid={`vehicule-card-${v.id}`}>
                  {/* Card Header */}
                  <div className="flex items-center justify-between p-4 cursor-pointer"
                    onClick={() => setSelected(isExpanded ? null : v)}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`${ICON_BOX} bg-[#1a237e]/10`}>
                        <Car className="w-5 h-5 text-[#1a237e]" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-slate-800 truncate">
                          {v.numero_unite && <span className="text-indigo-600 font-mono text-sm">{v.numero_unite} — </span>}
                          {v.nom}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{v.annee} {v.marque} {v.modele} · <span className="font-mono">{v.plaque}</span></p>
                        <div className="flex gap-1.5 mt-1 flex-wrap">
                          {v.assigne_a && (
                            <span className="text-[10px] bg-indigo-50 text-indigo-600 border border-indigo-200 px-1.5 py-0.5 rounded-full font-medium">
                              {v.assigne_a.type === 'sous_traitant' ? 'ST: ' : ''}{v.assigne_a.nom}
                            </span>
                          )}
                          {assStatus && <span className={`text-[10px] ${assStatus.color} px-1.5 py-0.5 rounded-full border border-current/20`}>Ass: {assStatus.label}</span>}
                          {inspStatus && <span className={`text-[10px] ${inspStatus.color} px-1.5 py-0.5 rounded-full border border-current/20`}>Insp: {inspStatus.label}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      {v.incidents_ouverts > 0 && (
                        <span className="flex items-center gap-0.5 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                          <AlertTriangle className="w-3 h-3" /> {v.incidents_ouverts}
                        </span>
                      )}
                      {canEdit && (
                        <>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-[#1a237e]"
                            onClick={e => { e.stopPropagation(); setSelected(v); setShowEdit(true); }}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                            onClick={e => { e.stopPropagation(); deleteVehicule(v.id); }}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-slate-100">
                      {/* Quick Stats */}
                      <div className="grid grid-cols-4 divide-x divide-slate-100 border-b border-slate-100 text-center bg-slate-50/50">
                        <div className="py-3">
                          <p className="text-[10px] text-slate-400 uppercase font-semibold">Kilométrage</p>
                          <p className="font-semibold text-slate-900 text-sm">{(v.kilometrage || 0).toLocaleString()} km</p>
                        </div>
                        <div className="py-3">
                          <p className="text-[10px] text-slate-400 uppercase font-semibold">Pneus hiver</p>
                          <div className="flex justify-center mt-0.5">{conditionBadge(v.pneus_hiver?.condition)}</div>
                        </div>
                        <div className="py-3">
                          <p className="text-[10px] text-slate-400 uppercase font-semibold">Pneus été</p>
                          <div className="flex justify-center mt-0.5">{conditionBadge(v.pneus_ete?.condition)}</div>
                        </div>
                        <div className="py-3">
                          <p className="text-[10px] text-slate-400 uppercase font-semibold">Incidents</p>
                          <p className={`font-semibold text-sm ${v.incidents_ouverts > 0 ? "text-red-600" : "text-emerald-600"}`}>{v.incidents_ouverts || 0}</p>
                        </div>
                      </div>
                      {/* Sub-tabs */}
                      <div className="p-4">
                        <Tabs defaultValue="entretiens">
                          <TabsList className="mb-4">
                            <TabsTrigger value="entretiens"><Wrench className="w-4 h-4 mr-1" />Entretiens</TabsTrigger>
                            <TabsTrigger value="incidents" data-testid="tab-incidents">
                              <AlertTriangle className="w-4 h-4 mr-1" />Incidents
                              {(v.incidents_ouverts || 0) > 0 && (
                                <span className="ml-1 bg-red-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">{v.incidents_ouverts}</span>
                              )}
                            </TabsTrigger>
                            <TabsTrigger value="materiel" data-testid="tab-materiel">
                              <Package className="w-4 h-4 mr-1" /> Matériel
                            </TabsTrigger>
                            <TabsTrigger value="inspection_rangement" data-testid="tab-inspection-rangement">
                              <ClipboardCheck className="w-4 h-4 mr-1" /> Inspection rangement
                            </TabsTrigger>
                          </TabsList>
                          <TabsContent value="entretiens">
                            <OngletEntretiens vehiculeId={v.id} vehiculeNom={v.nom || v.numero_unite || v.id} canEdit={canEdit} canDeclarer={true} />
                          </TabsContent>
                          <TabsContent value="incidents">
                            <OngletIncidents vehiculeId={v.id} vehiculeNom={v.nom} user={user} canCreate={canDeclareIncident} canEdit={canEdit} />
                          </TabsContent>
                          <TabsContent value="materiel">
                            <OngletMateriel uniteId={v.id} canEdit={canEdit} />
                          </TabsContent>
                          <TabsContent value="inspection_rangement">
                            <InspectionRangementSection unite={v} canEdit={canEdit} isChefEquipe={isChefEquipe} />
                          </TabsContent>
                        </Tabs>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* ─── Onglet Unités de Rangement — STACKED CARDS ─── */}
        {canEdit && (
          <TabsContent value="rangement">
            <div className="space-y-3 mt-4">
              {!uniteLoading && unites.length > 0 && (
                <div className="grid grid-cols-3 gap-2" data-testid="kpi-rangement">
                  <div className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-lg px-3 py-2.5">
                    <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0"><Package className="w-4 h-4 text-indigo-600" /></div>
                    <div><p className="text-lg font-bold text-slate-900 leading-none">{rngKpi.total}</p><p className="text-[10px] text-slate-500 uppercase font-medium mt-0.5">Unités</p></div>
                  </div>
                  <div className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-lg px-3 py-2.5">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${rngKpi.travail > 0 ? 'bg-indigo-100' : 'bg-slate-100'}`}>
                      <Car className={`w-4 h-4 ${rngKpi.travail > 0 ? 'text-indigo-600' : 'text-slate-400'}`} />
                    </div>
                    <div><p className={`text-lg font-bold leading-none ${rngKpi.travail > 0 ? 'text-indigo-600' : 'text-slate-400'}`}>{rngKpi.travail}</p><p className="text-[10px] text-slate-500 uppercase font-medium mt-0.5">Assignables</p></div>
                  </div>
                  <div className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-lg px-3 py-2.5">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${rngKpi.echafauds > 0 ? 'bg-orange-100' : 'bg-slate-100'}`}>
                      <Wrench className={`w-4 h-4 ${rngKpi.echafauds > 0 ? 'text-orange-600' : 'text-slate-400'}`} />
                    </div>
                    <div><p className={`text-lg font-bold leading-none ${rngKpi.echafauds > 0 ? 'text-orange-600' : 'text-slate-400'}`}>{rngKpi.echafauds}</p><p className="text-[10px] text-slate-500 uppercase font-medium mt-0.5">Échafauds</p></div>
                  </div>
                </div>
              )}
              {uniteLoading ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" /></div>
              ) : unites.length === 0 ? (
                <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-xl" data-testid="rangement-empty">
                  <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-sm text-slate-500 font-medium">Aucune unité de rangement créée</p>
                  <p className="text-xs text-slate-400 mt-1">Cliquez sur "Ajouter unité" pour créer votre première unité</p>
                </div>
              ) : unites.map(u => {
                const isExpanded = selectedUniteId === u.id;
                const isEditing = editUnite?.id === u.id;
                return (
                  <div key={u.id} className={`${CARD_BASE} ${isExpanded || isEditing ? 'border-indigo-300 shadow-md ring-1 ring-indigo-100' : CARD_IDLE}`} data-testid={`unite-card-${u.id}`}>
                    {isEditing ? (
                      /* Mode édition */
                      <div className="p-4 space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                          <div><label className="text-xs font-medium text-slate-600">Numéro/Nom *</label>
                            <Input value={editUnite.numero} onChange={e => setEditUnite(p => ({...p, numero: e.target.value}))} className="mt-0.5 h-8 text-sm" /></div>
                          <div><label className="text-xs font-medium text-slate-600"># d'unité</label>
                            <Input value={editUnite.numero_unite || ''} onChange={e => setEditUnite(p => ({...p, numero_unite: e.target.value}))} placeholder="U-001" className="mt-0.5 h-8 text-sm font-mono" /></div>
                          <div><label className="text-xs font-medium text-slate-600">Description</label>
                            <Input value={editUnite.description || ''} onChange={e => setEditUnite(p => ({...p, description: e.target.value}))} className="mt-0.5 h-8 text-sm" /></div>
                        </div>
                        <div className="grid grid-cols-3 gap-3 bg-slate-50 p-3 rounded-lg">
                          <div><label className="text-xs text-slate-500"># Plaque</label>
                            <Input value={editUnite.plaque || ''} onChange={e => setEditUnite(p => ({...p, plaque: e.target.value}))} placeholder="ABC-1234" className="mt-0.5 h-7 text-xs" /></div>
                          <div><label className="text-xs text-slate-500"># Série</label>
                            <Input value={editUnite.numero_serie || ''} onChange={e => setEditUnite(p => ({...p, numero_serie: e.target.value}))} placeholder="SN123456" className="mt-0.5 h-7 text-xs" /></div>
                          <div><label className="text-xs text-slate-500">Assurance (exp.)</label>
                            <Input type="date" value={editUnite.assurance_expiration || ''} onChange={e => setEditUnite(p => ({...p, assurance_expiration: e.target.value}))} className="mt-0.5 h-7 text-xs" /></div>
                        </div>
                        <div className="flex gap-1.5">
                          {[{val:'interieur',label:'Int.'},{val:'exterieur',label:'Ext.'},{val:'mixte',label:'Mixte'}].map(opt => (
                            <button key={opt.val} type="button" onClick={() => setEditUnite(p => ({...p, type_utilisation: opt.val}))}
                              className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${editUnite.type_utilisation === opt.val ? 'bg-indigo-100 border-indigo-400 text-indigo-800' : 'bg-white border-slate-200 text-slate-500'}`}>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                        <label className="flex items-center gap-2 text-xs cursor-pointer">
                          <input type="checkbox" checked={editUnite.est_remorque_echafauds || false} onChange={e => setEditUnite(p => ({
                            ...p, 
                            est_remorque_echafauds: e.target.checked,
                            // Initialiser les nouveaux champs si activé
                            types_etages: e.target.checked && (!p.types_etages || p.types_etages.length === 0) ? [] : (p.types_etages || []),
                            inventaire_pieces: e.target.checked && (!p.inventaire_pieces || p.inventaire_pieces.length === 0) ? [] : (p.inventaire_pieces || []),
                          }))} className="rounded" />
                          Remorque d'échafauds
                        </label>
                        
                        {/* NOUVELLE INTERFACE ÉCHAFAUDAGES - FORCÉE POUR TOUTES LES REMORQUES */}
                        {editUnite.est_remorque_echafauds && (
                          <EchafaudsInventaireUI
                            unite={editUnite}
                            onChange={(patch) => setEditUnite((p) => ({ ...p, ...patch }))}
                          />
                        )}
                        <label className="flex items-center gap-2 text-xs cursor-pointer font-medium text-indigo-700">
                          <input type="checkbox" checked={editUnite.est_unite_travail || false} onChange={e => setEditUnite(p => ({...p, est_unite_travail: e.target.checked}))} className="rounded border-indigo-400 text-indigo-600" />
                          Assignable à un chef d'équipe (Unité de travail)
                        </label>
                        <div className="flex gap-2 pt-1">
                          <Button size="sm" onClick={saveEditUnite} className="bg-indigo-600 hover:bg-indigo-700 text-white"><Save className="w-3 h-3 mr-1" /> Enregistrer</Button>
                          <Button size="sm" variant="outline" onClick={() => setEditUnite(null)}>Annuler</Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Card Header */}
                        <div className="flex items-center justify-between p-4 cursor-pointer"
                          onClick={() => setSelectedUniteId(isExpanded ? null : u.id)}>
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`${ICON_BOX} ${u.est_remorque_echafauds ? 'bg-orange-50 border border-orange-100' : 'bg-indigo-50 border border-indigo-100'}`}>
                              {u.est_remorque_echafauds ? <Wrench className="w-5 h-5 text-orange-600" /> : <Package className="w-5 h-5 text-indigo-600" />}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-bold text-slate-800 truncate">
                                  {u.numero_unite && <span className="text-indigo-600 font-mono text-sm">{u.numero_unite} — </span>}
                                  {u.numero}
                                </p>
                              </div>
                              {u.description && <p className="text-xs text-slate-500 truncate">{u.description}</p>}
                              <div className="flex gap-1.5 mt-1 flex-wrap">
                                {u.type_utilisation && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${
                                    u.type_utilisation === 'interieur' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                    u.type_utilisation === 'exterieur' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                    'bg-purple-50 text-purple-700 border-purple-200'
                                  }`}>
                                    {u.type_utilisation === 'interieur' ? 'Intérieur' : u.type_utilisation === 'exterieur' ? 'Extérieur' : 'Mixte'}
                                  </span>
                                )}
                                {u.est_unite_travail && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 font-medium">Travail</span>}
                                {u.est_remorque_echafauds && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200 font-medium">Échafauds</span>}
                                {u.plaque && <span className="text-[10px] font-mono text-slate-500">{u.plaque}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0 ml-2">
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-indigo-600"
                              onClick={e => { e.stopPropagation(); setEditUnite({...u}); }} data-testid={`edit-rangement-${u.id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                              onClick={e => { e.stopPropagation(); deleteUnite(u.id, u.numero); }} data-testid={`btn-delete-unite-${u.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                          </div>
                        </div>
                        {/* Expanded — Matériel */}
                        {isExpanded && (
                          <div className="border-t border-slate-100 px-4 pb-4 pt-3 bg-slate-50/50">
                            <OngletMateriel uniteId={u.id} canEdit={canEdit} />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>
        )}

        {/* ─── Onglet Unités d'élévation ─── */}
        <TabsContent value="elevation">
          <OngletElevation canEdit={canEdit} showCreate={showCreateNacelle} onCloseCreate={setShowCreateNacelle} />
        </TabsContent>
      </Tabs>

      {/* Dialog créer véhicule */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Ajouter une unité de travail</DialogTitle></DialogHeader>
          <VehiculeForm onSave={createVehicule} onCancel={() => setShowCreate(false)} />
        </DialogContent>
      </Dialog>

      {/* Dialog éditer véhicule */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifier le véhicule</DialogTitle></DialogHeader>
          {selected && <VehiculeForm initial={selected} onSave={updateVehicule} onCancel={() => setShowEdit(false)} />}
        </DialogContent>
      </Dialog>

      {/* Dialog créer unité de rangement */}
      <Dialog open={showCreateRangement} onOpenChange={setShowCreateRangement}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouvelle unité de rangement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Numéro / Nom *</Label>
                <Input value={newUniteNumero} onChange={e => setNewUniteNumero(e.target.value)}
                  placeholder="Ex: Unité 1, Van A, Boîte #3..." className="mt-1" data-testid="input-unite-numero"
                  onKeyDown={e => e.key === "Enter" && createUnite()} /></div>
              <div><Label># d'unité</Label>
                <Input value={newUniteCode} onChange={e => setNewUniteCode(e.target.value)}
                  placeholder="U-001" className="mt-1 font-mono" data-testid="input-unite-code" /></div>
            </div>
            <div>
              <Label>Description (optionnel)</Label>
              <Input value={newUniteDesc} onChange={e => setNewUniteDesc(e.target.value)}
                placeholder="Ex: Équipe rouge, peinture ext." className="mt-1" data-testid="input-unite-desc" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs"># Plaque</Label>
                <Input value={newUnitePlaque} onChange={e => setNewUnitePlaque(e.target.value.toUpperCase())} placeholder="ABC-1234" className="mt-1" /></div>
              <div><Label className="text-xs"># Série</Label>
                <Input value={newUniteSerie} onChange={e => setNewUniteSerie(e.target.value)} placeholder="SN123456" className="mt-1" /></div>
              <div><Label className="text-xs">Assurance (exp.)</Label>
                <Input type="date" value={newUniteAssurance} onChange={e => setNewUniteAssurance(e.target.value)} className="mt-1" /></div>
            </div>
            <div>
              <Label className="mb-2 block">Type d'utilisation</Label>
              <div className="flex gap-2">
                {[
                  { val: 'interieur', label: 'Intérieur' },
                  { val: 'exterieur', label: 'Extérieur' },
                  { val: 'les_deux', label: 'Les deux' },
                ].map(opt => (
                  <button key={opt.val} type="button"
                    onClick={() => setNewUniteType(opt.val)}
                    className={`text-sm px-4 py-2 rounded-lg border font-medium transition-colors ${
                      newUniteType === opt.val ? 'bg-indigo-100 border-indigo-400 text-indigo-800' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400'
                    }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2 border-t pt-3">
              {/* 🆕 [Phase 86 Sprint Parking 1 — 2026-06-23] Type physique explicite */}
              <div data-testid="radio-type-physique-group" className="bg-amber-50 border border-amber-200 rounded p-2">
                <div className="text-xs font-semibold text-amber-900 mb-1.5">
                  Type physique <span className="text-amber-700">(détermine la catégorie Parking)</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  {[
                    { val: 'vehicule', label: '🚛 Véhicule' },
                    { val: 'remorque', label: '🧰 Remorque' },
                    { val: 'equipement_chantier', label: '🏗️ Équipement Chantier' },
                  ].map(opt => (
                    <label
                      key={opt.val}
                      className="flex items-center gap-1.5 text-sm cursor-pointer"
                      data-testid={`radio-type-physique-${opt.val}`}
                    >
                      <input
                        type="radio"
                        name="type-physique"
                        value={opt.val}
                        checked={newUniteTypePhysique === opt.val}
                        onChange={() => setNewUniteTypePhysique(opt.val)}
                        className="w-4 h-4 text-amber-600"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={newUniteRemorque} onChange={e => setNewUniteRemorque(e.target.checked)} className="w-4 h-4 rounded border-orange-400 text-orange-600" />
                Remorque d'échafauds
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer font-medium text-indigo-700" title="Option fonctionnelle : permet d'assigner l'unité à un chef d'équipe. N'influence plus la catégorie Parking (utiliser le radio Type physique au-dessus).">
                <input type="checkbox" checked={newUniteTravail} onChange={e => setNewUniteTravail(e.target.checked)} className="w-4 h-4 rounded border-indigo-400 text-indigo-600" />
                Assignable à un chef d'équipe <span className="text-xs text-slate-500">(option indépendante du type physique)</span>
              </label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCreateRangement(false)}>Annuler</Button>
            <Button onClick={createUnite} disabled={savingUnite} className="bg-[#1a237e] hover:bg-[#1a237e]/90 text-white" data-testid="btn-create-unite">
              {savingUnite ? 'Création...' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
